<!--
  工程设置弹窗组件
  职责：承载多页签设置框架，具体页签内容拆分到独立组件维护
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue'
import { NButton, NModal, NTabs, NTabPane, useMessage } from 'naive-ui'
import SettingsDialogTab from '@/components/workspace/settingsTabs/SettingsDialogTab.vue'
import SettingsGeneralTab from '@/components/workspace/settingsTabs/SettingsGeneralTab.vue'
import SettingsImageOptionTab from '@/components/workspace/settingsTabs/SettingsImageOptionTab.vue'
import SettingsMenuTab from '@/components/workspace/settingsTabs/SettingsMenuTab.vue'
import SettingsPaymentTab from '@/components/workspace/settingsTabs/SettingsPaymentTab.vue'
import SettingsReplayTab from '@/components/workspace/settingsTabs/SettingsReplayTab.vue'
import SettingsSaveLoadTab from '@/components/workspace/settingsTabs/SettingsSaveLoadTab.vue'
import SettingsSystemTab from '@/components/workspace/settingsTabs/SettingsSystemTab.vue'
import SettingsTitleTab from '@/components/workspace/settingsTabs/SettingsTitleTab.vue'
import { ENABLE_MALL_SETTINGS } from '@/config/featureFlags'
import {
  ensureFontLoadedFromVirtualResource,
  ensureProjectFontLoaded,
} from '@/features/project-font/runtime'
import { usePreviewStageState } from '@/features/workspace/settings/usePreviewStageState'
import {
  applySaveLoadFontFields,
  resolveSaveLoadPreviewFontSize,
  resolveSaveLoadStepperDisplaySize,
} from '@/features/workspace/settings/saveLoadFontSettings'
import { hexToRgbString, rgbStringToHex } from '@/utils/textColor'
import {
  resolvePortraitLayoutViewport,
  readLayoutViewportSize,
  readStableViewportHeight,
  WORKSPACE_TABLET_MAX_SHORT_SIDE,
} from '@/platform/viewport/layoutViewport'
import { createReservedSettingsImageOptionButton as createReservedImageOptionButtonDraft, useSettingsImageOptions } from '@/features/workspace/settings/useSettingsImageOptions'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'
import { getPreviewCenterOffset } from '@/features/preview/textLayout'
import { measureBitmapFontText } from '@/features/bitmap-font/metrics'
import {
  normalizeResourceBaseFolderPath,
  resolveResourceUrlFromFileName,
} from '@/features/virtual-resource/fileNamePath'
import FileName from '@/model/ComControl/FileName'
import AvgButton from '@/model/ComControl/AvgButton'
import AvgButtonIndex from '@/model/ComControl/AvgButtonIndex'
import Viewport from '@/model/ComControl/Viewport'
import BtnList from '@/model/GameSystemData/BtnList'
import type DataFileMap from '@/model/Resource/DataFileMap'
import type MainProject from '@/model/MainProject'
import MessageDialogData from '@/model/GameSystemData/MessageData/MessageDialogData'
import MusicItem from '@/model/ComControl/MusicItem'
import FontSetting from '@/model/GameSystemData/FontSetting'
import GameMenuData from '@/model/GameSystemData/GameMenuData'
import GameReplay from '@/model/GameSystemData/GameReplay'
import SaveLoadData from '@/model/GameSystemData/SaveLoadData'
import GameSettingData from '@/model/GameSystemData/GameSettingData'
import GameTitleData from '@/model/GameSystemData/GameTitleData'
import NameDialogData from '@/model/GameSystemData/MessageData/NameDialogData'
import TalkMessageData from '@/model/GameSystemData/MessageData/TalkMessageData'
import TotalSetting from '@/model/GameSystemData/TotalSetting'
import GoodPropertyModel from '@/model/GoodPropertyModel'
import GameMallNewModel from '@/model/MallNewClass/GameMallNewModel'
import Goods, { GoodsStatus, GoodsTypeEnum } from '@/model/MallNewClass/Goods'
import GoodDetailPageModel from '@/model/MallNewClass/GoodDetailPageModel'
import FontProperty from '@/model/MallNewClass/FontProperty'
import { useWorkspaceStore } from '@/store/workspace'
import { useSceneEditorStore } from '@/store/sceneEditor'
import {
  hasInvalidProjectNameChar,
  isProjectNameLengthValid,
  PROJECT_NAME_INVALID_CHAR_MESSAGE,
  PROJECT_NAME_LIMIT_MESSAGE,
} from '@/utils/projectNameLimit'
import { ensureMallNewForProject } from '@/features/project-mall/runtime'
import {
  DIALOG_GRAPHIC_BASE_FOLDER,
  IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  MALL_GOODS_PER_PAGE,
  MAX_MALL_GOODS_COUNT,
  MENU_BUTTON_LABELS,
  PAYMENT_GRAPHIC_BASE_FOLDER,
  SETTINGS_BACKGROUND_BASE_FOLDER,
  SYSTEM_AUDIO_DEFS,
  SYSTEM_BUTTON_DEFS,
  TITLE_BACKGROUND_BASE_FOLDER,
  TITLE_BUTTON_LABELS,
  TITLE_MUSIC_BASE_FOLDER,
} from '@/features/workspace/settings/settingsConstants'
import type {
  SettingsButtonDraft as AvgButtonDraft,
  SettingsButtonIndexDraft as AvgButtonIndexDraft,
  SettingsDialogMessageDraft as MessageDialogDraft,
  SettingsDialogModalDraft as TalkMessageDraft,
  SettingsDialogNameDraft as NameDialogDraft,
  SettingsFileDraft as FileNameDraft,
  SettingsFontSettingDraft as FontSettingDraft,
  SettingsMallPropertyDraft as GoodPropertyDraft,
  SettingsMallFontDraft as MallFontDraft,
  SettingsMenuModalDraft as MenuDraft,
  SettingsModalMusicDraft as MusicDraft,
  SettingsPaymentDetailDraft as GoodDetailPageDraft,
  SettingsPaymentGoodsDraft as MallGoodsDraft,
  SettingsPaymentModalDraft as MallDraft,
  SettingsPreviewButtonDraft as TitleButtonDraft,
  SettingsReplayModalDraft as ReplayDraft,
  SettingsSaveLoadDraft as SaveLoadDraft,
  SettingsSystemAudioDraft as SystemAudioDraft,
  SettingsSystemButtonDraft as SystemButtonDraft,
  SettingsSystemModalDraft as SystemDraft,
  SettingsTitleModalDraft as TitleDraft,
  SettingsTotalModalDraft as TotalSettingDraft,
  SettingsViewportDraft as ViewportDraft,
} from '@/features/workspace/settings/types'

type SettingsTabKey =
  | 'general'
  | 'title'
  | 'menu'
  | 'dialog'
  | 'image-option'
  | 'replay'
  | 'save-load'
  | 'system'
  | 'payment'

const MENU_OPTIONS = [
  { value: 10001, label: '[系统保留]工程菜单' },
]

const props = defineProps<{
  /** 是否显示弹窗 */
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
}>()

const workspace = useWorkspaceStore()
const editor = useSceneEditorStore()
const message = useMessage()
const project = computed(() => workspace.project)
const PORTRAIT_TABS_HEIGHT = 38
const PORTRAIT_ACTIONS_HEIGHT = 34
const DEFAULT_PORTRAIT_HEADER_HEIGHT = 47

const isSettingsPortraitLayout = ref(false)
const portraitPreviewHeight = ref(260)
const settingsViewportHeight = ref(0)
const settingsModalHeaderHeight = ref(DEFAULT_PORTRAIT_HEADER_HEIGHT)
const projectScreenWidth = computed(() => Math.max(1, Number(project.value?.screenHash.width ?? 1280)))
const projectScreenHeight = computed(() => Math.max(1, Number(project.value?.screenHash.height ?? 720)))
const settingsTab = ref<SettingsTabKey>('general')
const projectNameDraft = ref('')
const generalDraft = ref<TotalSettingDraft | null>(null)
const titleDraft = ref<TitleDraft | null>(null)
const menuDraft = ref<MenuDraft | null>(null)
const dialogDraft = ref<TalkMessageDraft | null>(null)
const replayDraft = ref<ReplayDraft | null>(null)
const saveLoadDraft = ref<SaveLoadDraft | null>(null)
const systemDraft = ref<SystemDraft | null>(null)
const paymentDraft = ref<MallDraft | null>(null)
const totalSetting = computed(() => generalDraft.value)
const titleSetting = computed(() => titleDraft.value)
const menuSetting = computed(() => menuDraft.value)
const talkSetting = computed(() => dialogDraft.value)
const replaySetting = computed(() => replayDraft.value)
const saveLoadSetting = computed(() => saveLoadDraft.value)
const systemSetting = computed(() => systemDraft.value)
const paymentSetting = computed(() => paymentDraft.value)
const previewFontFamily = ref('')
const saveLoadPreviewFontFamily = ref('')
let defaultFontPreviewToken = 0
let saveLoadFontPreviewToken = 0
/** 用户是否在本轮设置弹窗中改过存读档字体/字号；未改时保存不得覆盖工程原值。 */
const saveLoadFontDirty = ref(false)
const previewNameImageSize = ref({ width: 0, height: 0 })
const previewTalkImageSize = ref({ width: 0, height: 0 })
const previewReplayCloseImageSize = ref({ width: 0, height: 0 })
const previewTitleButtonImageSizes = ref<Record<string, { width: number; height: number }>>({})
const previewSystemBarNoneImageSize = ref({ width: 0, height: 0 })
const previewSystemBarMoveImageSize = ref({ width: 0, height: 0 })
const paymentDecorateImageSize = ref({ width: 0, height: 0 })
const paymentGoodAreaImageSize = ref({ width: 0, height: 0 })

const { stageScales, resizeHandlers } = usePreviewStageState(projectScreenWidth, projectScreenHeight)
const handleTitlePreviewStageResize = resizeHandlers.title
const handleMenuPreviewStageResize = resizeHandlers.menu
const handleDialogPreviewStageResize = resizeHandlers.dialog
const handleReplayPreviewStageResize = resizeHandlers.replay
const handleSaveLoadPreviewStageResize = resizeHandlers.saveLoad
const handleSystemPreviewStageResize = resizeHandlers.system
const handlePaymentPreviewStageResize = resizeHandlers.payment

const {
  imageOptionDraft,
  imageOptionButtons,
  imageOptionMaxCount,
  imageOptionSelectedIndex,
  imageOptionHover,
  imageOptionPickerItems,
  isReservedImageOption,
  selectedImageOption,
  selectedImageOptionNoneUrl,
  selectedImageOptionMoveUrl,
  imageOptionTestUrl,
  showImageOptionButtonTest,
  setImageOptionButtons,
  resetImageOptions,
  selectImageOption,
  formatImageOptionLabel,
  updateSelectedImageOptionNonePic,
  updateSelectedImageOptionMovePic,
  resolveImageOptionPreviewUrl,
} = useSettingsImageOptions({
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  resolveGraphicPreviewUrl,
  titleSetting,
  menuSetting,
  systemSetting,
  replaySetting,
  saveLoadSetting,
  applyReplayButtonIndex,
})

const settingsModalClass = computed(() => [
  'modal-settings',
  {
    'modal-settings--portrait': isSettingsPortraitLayout.value,
  },
])
const settingsShellStyle = computed<CSSProperties>(() => ({
  '--settings-portrait-preview-h': `${portraitPreviewHeight.value}px`,
  '--settings-portrait-preview-aspect': `${projectScreenWidth.value} / ${projectScreenHeight.value}`,
}))

/** 实测设置弹窗标题栏高度，避免写死 47px 导致内容区溢出。 */
function measureSettingsModalHeaderHeight(): number {
  if (typeof document === 'undefined') return DEFAULT_PORTRAIT_HEADER_HEIGHT
  const modalEl = document.querySelector('.modal-settings--portrait.n-modal')
  const headerEl = modalEl?.querySelector('.n-card-header')
  if (!headerEl) return DEFAULT_PORTRAIT_HEADER_HEIGHT
  const height = Math.ceil(headerEl.getBoundingClientRect().height)
  return height > 0 ? height : DEFAULT_PORTRAIT_HEADER_HEIGHT
}

function updatePortraitPreviewHeight(width: number): void {
  const viewportH = settingsViewportHeight.value || readStableViewportHeight()
  const headerH = settingsModalHeaderHeight.value
  const contentHeight = Math.max(
    1,
    viewportH - headerH - PORTRAIT_TABS_HEIGHT - PORTRAIT_ACTIONS_HEIGHT,
  )
  const widthBasedHeight = Math.round(width * projectScreenHeight.value / projectScreenWidth.value)
  portraitPreviewHeight.value = Math.max(150, Math.round(Math.min(contentHeight * 0.5, widthBasedHeight)))
}

function updateSettingsPortraitLayout(): void {
  if (typeof window === 'undefined') return
  const layout = readLayoutViewportSize()
  settingsViewportHeight.value = readStableViewportHeight()
  isSettingsPortraitLayout.value = resolvePortraitLayoutViewport(
    WORKSPACE_TABLET_MAX_SHORT_SIDE,
    isSettingsPortraitLayout.value,
  )
  if (!isSettingsPortraitLayout.value) return
  updatePortraitPreviewHeight(layout.width)
}

/** 弹窗展示后重测标题栏，并同步竖屏 shell 用的 CSS 变量。 */
async function refreshSettingsPortraitChromeMetrics(): Promise<void> {
  updateSettingsPortraitLayout()
  if (!props.show || !isSettingsPortraitLayout.value) return
  await nextTick()
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
  settingsModalHeaderHeight.value = measureSettingsModalHeaderHeight()
  const layout = readLayoutViewportSize()
  updatePortraitPreviewHeight(layout.width)
}

const settingsModalPortraitStyle = computed<CSSProperties>(() => {
  if (!isSettingsPortraitLayout.value) return {}
  const viewportH = settingsViewportHeight.value || readStableViewportHeight()
  const headerH = settingsModalHeaderHeight.value
  const bodyH = Math.max(1, viewportH - headerH)
  const contentH = Math.max(1, viewportH - headerH - PORTRAIT_TABS_HEIGHT - PORTRAIT_ACTIONS_HEIGHT)
  return {
    '--settings-viewport-h': `${viewportH}px`,
    '--settings-modal-header-h': `${headerH}px`,
    '--settings-portrait-body-h': `${bodyH}px`,
    '--settings-portrait-content-h': `${contentH}px`,
    '--settings-portrait-tabs-h': `${PORTRAIT_TABS_HEIGHT}px`,
    '--settings-portrait-actions-h': `${PORTRAIT_ACTIONS_HEIGHT}px`,
  }
})

function handleSettingsViewportResize(): void {
  void refreshSettingsPortraitChromeMetrics()
}

function cloneFileNameDraft(fileName: FileName | null | undefined) {
  return {
    type: Number(fileName?.type ?? FileName.DEF_TYPE),
    name: fileName?.name ?? '',
  }
}

function cloneMusicDraft(item: MusicItem | null | undefined): MusicDraft {
  return {
    type: Number(item?.Name?.type ?? FileName.DEF_TYPE),
    name: item?.Name?.name ?? '',
    volume: Number(item?.Volume ?? 80),
  }
}

function cloneFontSettingDraft(item: FontSetting | null | undefined): FontSettingDraft {
  return {
    Font: item?.Font ?? '',
    FontSize: Number(item?.FontSize ?? 30),
    Height: Number(item?.Height ?? 36),
  }
}

function cloneMallFontDraft(item: FontProperty | null | undefined, fallbackSize: number): MallFontDraft {
  const size = Number(item?.Font_Size ?? 0)
  const name = String(item?.Font_name ?? '').trim()
  const color = String(item?.Font_color ?? '').trim()
  return {
    Font_Size: size > 0 ? size : fallbackSize,
    Font_name: name || '微软雅黑',
    Font_color: color || '255,255,255',
  }
}

function cloneNameDialogDraft(source: NameDialogData | null | undefined): NameDialogDraft {
  return {
    x: Number(source?.x ?? 0),
    y: Number(source?.y ?? 0),
    BackPic: cloneFileNameDraft(source?.BackPic),
    P_x: Number(source?.P_x ?? 0),
    P_y: Number(source?.P_y ?? 0),
    IsCenter: Boolean(source?.IsCenter),
  }
}

function cloneMessageDialogDraft(source: MessageDialogData | null | undefined): MessageDialogDraft {
  return {
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
    BackPic: cloneFileNameDraft(source?.BackPic),
    FacePic: cloneFileNameDraft(source?.FacePic),
    f_x: Number(source?.f_x ?? 0),
    f_y: Number(source?.f_y ?? 0),
    p_x1: Number(source?.p_x1 ?? 0),
    p_y1: Number(source?.p_y1 ?? 0),
    p_viewW: Number(source?.p_viewW ?? 0),
    p_viewH: Number(source?.p_viewH ?? 0),
    Buttons: [...(source?.Buttons ?? [])],
    ButtonsEx: [...(source?.ButtonsEx ?? [])],
    BIndex: [...(source?.BIndex ?? [])],
  }
}

function createDialogDraft(source: TalkMessageData | null | undefined): TalkMessageDraft {
  const selectButton = Number(source?.SelectButton ?? 0)
  const selectPic = Number(source?.SelectPic ?? 0)
  return {
    FaceStyle: Number(source?.FaceStyle ?? 0),
    SelectButton: selectButton,
    SelectPic: selectPic || selectButton,
    NameWinS: cloneNameDialogDraft(source?.NameWinS),
    TalkWin: cloneMessageDialogDraft(source?.TalkWin),
  }
}

function cloneAvgButtonDraft(source: AvgButton | null | undefined): AvgButtonDraft {
  return {
    Name: source?.Name ?? '',
    NoneImage: cloneFileNameDraft(source?.NoneImage),
    MoveImage: cloneFileNameDraft(source?.MoveImage),
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
  }
}

function createImageOptionDraft(source: AvgButton[] | null | undefined): AvgButtonDraft[] {
  const next = (source ?? []).map((item) => cloneAvgButtonDraft(item))
  if (!next.length) return [createReservedImageOptionButtonDraft()]
  next[0] = {
    ...next[0],
    Name: next[0].Name?.trim() || '[系统保留]未使用',
  }
  return next
}

function cloneViewportDraft(source: Viewport | null | undefined): ViewportDraft {
  return {
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
    Width: Number(source?.Width ?? 0),
    Height: Number(source?.Height ?? 0),
  }
}

function cloneAvgButtonIndexDraft(source: AvgButtonIndex | null | undefined): AvgButtonIndexDraft {
  return {
    Index: Number(source?.Index ?? 0),
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
  }
}

function cloneGoodPropertyDraft(source: GoodPropertyModel | null | undefined): GoodPropertyDraft {
  return {
    PropertyId: Number(source?.PropertyId ?? -1),
    Operation: Number(source?.Operation ?? -1),
    Number: Number(source?.Number ?? -1),
  }
}

function createGoodDetailPageDraft(source: GoodDetailPageModel | null | undefined): GoodDetailPageDraft {
  return {
    DetailGraph: cloneFileNameDraft(source?.DetailGraph),
    PreviewBackGraph: cloneFileNameDraft(source?.PreviewBackGraph),
    PreviewBack_X: Number(source?.PreviewBack_X ?? 0),
    PreviewBack_Y: Number(source?.PreviewBack_Y ?? 0),
    PreviewBack_W: Math.max(1, Number(source?.PreviewBack_W ?? 160)),
    PreviewBack_H: Math.max(1, Number(source?.PreviewBack_H ?? 160)),
    Preview_X: Number(source?.Preview_X ?? 0),
    Preview_Y: Number(source?.Preview_Y ?? 0),
    Preview_W: Math.max(1, Number(source?.Preview_W ?? 128)),
    Preview_H: Math.max(1, Number(source?.Preview_H ?? 128)),
    GoodName_X: Number(source?.GoodName_X ?? 0),
    GoodName_Y: Number(source?.GoodName_Y ?? 0),
    FlowerTotal_X: Number(source?.FlowerTotal_X ?? 0),
    FlowerTotal_Y: Number(source?.FlowerTotal_Y ?? 0),
    Flower_X: Number(source?.Flower_X ?? 0),
    Flower_Y: Number(source?.Flower_Y ?? 0),
    GoodPrice_X: Number(source?.GoodPrice_X ?? 0),
    GoodPrice_Y: Number(source?.GoodPrice_Y ?? 0),
    BuyCount_X: Number(source?.BuyCount_X ?? 0),
    BuyCount_Y: Number(source?.BuyCount_Y ?? 0),
    GoodIntroView: cloneViewportDraft(source?.GoodIntroView),
    Ok_btn: cloneAvgButtonIndexDraft(source?.Ok_btn),
    Cancel_btn: cloneAvgButtonIndexDraft(source?.Cancel_btn),
    Add_btn: cloneAvgButtonIndexDraft(source?.Add_btn),
    Minus_btn: cloneAvgButtonIndexDraft(source?.Minus_btn),
    IsUseImageIndex: Boolean(source?.IsUseImageIndex),
    Index: Number(source?.Index ?? 1),
    IndexImage: cloneFileNameDraft(source?.IndexImage),
    IsShowBuyCount: Boolean(source?.IsShowBuyCount),
    IsShowGoodName: Boolean(source?.IsShowGoodName),
    IsShowGoodIntro: Boolean(source?.IsShowGoodIntro),
    IsShowGoodImage: Boolean(source?.IsShowGoodImage),
  }
}

function createReplayDraft(source: GameReplay | null | undefined): ReplayDraft {
  const closeEx = cloneAvgButtonIndexDraft(source?.CloseEx)
  return {
    BackPic: cloneFileNameDraft(source?.BackPic),
    Viewport: cloneViewportDraft(source?.Viewport),
    Close: cloneAvgButtonDraft(source?.Close),
    CloseEx: closeEx,
    ButtonIndex: Number(source?.ButtonIndex ?? closeEx.Index ?? 0),
  }
}

function createSaveLoadDraft(source: SaveLoadData | null | undefined): SaveLoadDraft {
  return {
    BackPic: cloneFileNameDraft(source?.BackPic),
    Viewport: cloneViewportDraft(source?.Viewport),
    Max: Math.max(1, Number(source?.Max ?? 6)),
    Column: Math.max(1, Number(source?.Column ?? 2)),
    SpanRow: Math.max(1, Number(source?.SpanRow ?? 122)),
    SpanCol: Math.max(1, Number(source?.SpanCol ?? 240)),
    BackButtonEx: cloneAvgButtonIndexDraft(source?.BackButtonEx),
    CloseButtonEx: cloneAvgButtonIndexDraft(source?.CloseButtonEx),
    IsMapName: Boolean(source?.IsMapName),
    IsDate: Boolean(source?.IsDate),
    IsMinPic: Boolean(source?.IsMinPic),
    Name_X: Number(source?.Name_X ?? 0),
    Name_Y: Number(source?.Name_Y ?? 0),
    Date_X: Number(source?.Date_X ?? 0),
    Date_Y: Number(source?.Date_Y ?? 0),
    Pic_X: Number(source?.Pic_X ?? 0),
    Pic_Y: Number(source?.Pic_Y ?? 0),
    Zoom: Math.max(1, Number(source?.Zoom ?? 10)),
    IsPage: Boolean(source?.IsPage),
    PrePic: cloneAvgButtonIndexDraft(source?.PrePic),
    NextPic: cloneAvgButtonIndexDraft(source?.NextPic),
    PageX: Number(source?.PageX ?? 0),
    PageY: Number(source?.PageY ?? 0),
    Font: source?.CundangFontName ?? '',
    FontSize: Number(source?.CundangFontSize ?? 0),
  }
}

function createMallGoodsDraft(source: Goods | null | undefined): MallGoodsDraft {
  const goodsBackWidth = Math.max(1, Number(source?.GoodsBack_W ?? 160))
  const goodsBackHeight = Math.max(1, Number(source?.GoodsBack_H ?? 92))
  const previewColumnRight = Math.max(
    Number(source?.PreviewBack_X ?? 0) + Math.max(1, Number(source?.PreviewBack_W ?? 54)),
    Number(source?.Preview_X ?? 0) + Math.max(1, Number(source?.Preview_W ?? 48)),
  )
  const defaultTextX = Math.min(Math.max(previewColumnRight + 8, 62), Math.max(12, goodsBackWidth - 84))
  const defaultTextWidth = Math.max(72, goodsBackWidth - defaultTextX - 8)
  const rawIntroView = cloneViewportDraft(source?.GoodIntroView)
  const shouldNormalizeIntroView =
    rawIntroView.Width <= 0
    || rawIntroView.Height <= 0
    || rawIntroView.Width > Math.max(120, goodsBackWidth)
    || rawIntroView.Height > Math.max(80, goodsBackHeight)
  const normalizedIntroView = shouldNormalizeIntroView
    ? {
        X: defaultTextX,
        Y: 32,
        Width: defaultTextWidth,
        Height: Math.max(24, Math.min(30, goodsBackHeight - 40)),
      }
    : rawIntroView
  const goodsType = Number(source?.GoodsType ?? GoodsTypeEnum.Var)
  return {
    GoodsId: Number(source?.GoodsId ?? 0),
    GoodsName: source?.GoodsName ?? '',
    GoodsFont: cloneMallFontDraft(source?.GoodsFont, 21),
    GoodsName_X: Number(source?.GoodsName_X ?? 0),
    GoodsName_Y: Number(source?.GoodsName_Y ?? 0),
    GoodsType: goodsType,
    GoodsPrice: Number(source?.GoodsPrice ?? 0),
    GoodsPricePakoras: Number(source?.GoodsPricePakoras ?? 0),
    GoodsPriceFont: cloneMallFontDraft(source?.GoodsPriceFont, 18),
    GoodsPrice_X: Number(source?.GoodsPrice_X ?? 0),
    GoodsPrice_Y: Number(source?.GoodsPrice_Y ?? 0),
    GoodsIntro: source?.GoodsIntro ?? '',
    GoodsIntroFont: cloneMallFontDraft(source?.GoodsIntroFont, 18),
    GoodIntroView: normalizedIntroView,
    Flower_X: Number(source?.Flower_X ?? 0),
    Flower_Y: Number(source?.Flower_Y ?? 0),
    Good_Status: Number(source?.Good_Status ?? 1),
    CanBuyRepeat: goodsType === GoodsTypeEnum.Var && Boolean(source?.CanBuyRepeat),
    GoodBack: cloneFileNameDraft(source?.GoodBack),
    GoodsBack_X: Number(source?.GoodsBack_X ?? 0),
    GoodsBack_Y: Number(source?.GoodsBack_Y ?? 0),
    GoodsBack_W: Math.max(1, Number(source?.GoodsBack_W ?? 160)),
    GoodsBack_H: Math.max(1, Number(source?.GoodsBack_H ?? 92)),
    Preview: cloneFileNameDraft(source?.Preview),
    Preview_X: Number(source?.Preview_X ?? 0),
    Preview_Y: Number(source?.Preview_Y ?? 0),
    Preview_W: Math.max(1, Number(source?.Preview_W ?? 48)),
    Preview_H: Math.max(1, Number(source?.Preview_H ?? 48)),
    PreviewBack: cloneFileNameDraft(source?.PreviewBack),
    PreviewBack_X: Number(source?.PreviewBack_X ?? 0),
    PreviewBack_Y: Number(source?.PreviewBack_Y ?? 0),
    PreviewBack_W: Math.max(1, Number(source?.PreviewBack_W ?? 54)),
    PreviewBack_H: Math.max(1, Number(source?.PreviewBack_H ?? 54)),
    BuyBtn: cloneAvgButtonIndexDraft(source?.BuyBtn),
    BoughtBtn: cloneFileNameDraft(source?.BoughtBtn),
    NoUpPic: cloneFileNameDraft(source?.NoUpPic),
    PropertyList: (source?.PropertyList ?? []).map((item) => cloneGoodPropertyDraft(item)),
  }
}

function createPaymentDraft(source: MainProject['GameMallNew'] | null | undefined): MallDraft {
  const draft = {
    MallBack: cloneFileNameDraft(source?.MallBack),
    DecorateBack: cloneFileNameDraft(source?.DecorateBack),
    Decorate_X: Number(source?.Decorate_X ?? 0),
    Decorate_Y: Number(source?.Decorate_Y ?? 0),
    GoodAreaBack: cloneFileNameDraft(source?.GoodAreaBack),
    GoodAreaBack_X: Number(source?.GoodAreaBack_X ?? 0),
    GoodAreaBack_Y: Number(source?.GoodAreaBack_Y ?? 0),
    GoodAreaBack_W: Math.max(1, Number(source?.GoodAreaBack_W ?? 320)),
    GoodAreaBack_H: Math.max(1, Number(source?.GoodAreaBack_H ?? 320)),
    ClosePic: cloneAvgButtonIndexDraft(source?.ClosePic),
    FlowerPic: cloneFileNameDraft(source?.FlowerPic),
    Flower_X: Number(source?.Flower_X ?? 0),
    Flower_Y: Number(source?.Flower_Y ?? 0),
    FlowerOwn_X: Number(source?.FlowerOwn_X ?? 0),
    FlowerOwn_Y: Number(source?.FlowerOwn_Y ?? 0),
    FlowerPrice_X: Number(source?.FlowerPrice_X ?? 0),
    FlowerPrice_Y: Number(source?.FlowerPrice_Y ?? 0),
    PrePic: cloneAvgButtonIndexDraft(source?.PrePic),
    NextPic: cloneAvgButtonIndexDraft(source?.NextPic),
    PageX: Number(source?.PageX ?? 0),
    PageY: Number(source?.PageY ?? 0),
    TagButton: cloneAvgButtonIndexDraft(source?.TagButton),
    TagName: source?.TagName ?? '鲜花商城',
    IsShowGoodName: Boolean(source?.IsShowGoodName),
    IsShowGoodIntro: Boolean(source?.IsShowGoodIntro),
    IsShowGoodImage: Boolean(source?.IsShowGoodImage),
    IsShowOneKeyGetBtn: Boolean(source?.IsShowOneKeyGetBtn),
    OneKeyGetBtn: cloneAvgButtonIndexDraft(source?.OneKeyGetBtn),
    GoodDetailPage: createGoodDetailPageDraft(source?.GoodDetailPage),
    GoodsList: (source?.GoodsList ?? []).map((item) => createMallGoodsDraft(item)),
  }
  return draft
}

function createTitleDraft(source: GameTitleData | null | undefined): TitleDraft {
  return {
    IsLogo: Boolean(source?.IsLogo),
    LogoPic: cloneFileNameDraft(source?.LogoPic),
    TitlePic: cloneFileNameDraft(source?.TitlePic),
    TitleBGM: cloneMusicDraft(source?.TitleBGM),
    IsDrawText: Boolean(source?.IsDrawText),
    IsFree: true,
    Buttons: TITLE_BUTTON_LABELS.map((label, index) => {
      const buttonEx = source?.ButtonsEx?.[index]
      const button = source?.Buttons?.[index]
      const buttonIndex = Number(buttonEx?.Index ?? 0)
      return {
        label,
        enabled: buttonIndex > 0,
        index: buttonIndex,
        x: Number(buttonEx?.X ?? button?.X ?? 0),
        y: Number(buttonEx?.Y ?? button?.Y ?? 0),
      }
    }),
  }
}

function createMenuDraft(source: GameMenuData | null | undefined): MenuDraft {
  return {
    BackPic: cloneFileNameDraft(source?.BackPic),
    IsFree: true,
    Buttons: MENU_BUTTON_LABELS.map((label, index) => {
      const buttonEx = source?.ButtonsEx?.[index]
      const button = source?.Buttons?.[index]
      const buttonIndex = Number(buttonEx?.Index ?? 0)
      return {
        label,
        enabled: buttonIndex > 0,
        index: buttonIndex,
        x: Number(buttonEx?.X ?? button?.X ?? 0),
        y: Number(buttonEx?.Y ?? button?.Y ?? 0),
      }
    }),
  }
}

function createSystemDraft(source: GameSettingData | null | undefined): SystemDraft {
  return {
    BackPic: cloneFileNameDraft(source?.BackPic),
    BarNone: cloneFileNameDraft(source?.BarNone),
    BarMove: cloneFileNameDraft(source?.BarMove),
    ButtonsIndex: [...(source?.ButtonsIndex ?? [])],
    IsFullScreen: Boolean(source?.IsFullScreen),
    IsAutoText: Boolean(source?.IsAutoText),
    IsBGM: Boolean(source?.IsBGM),
    IsSE: Boolean(source?.IsSE),
    IsVoice: Boolean(source?.IsVoice),
    IsTitle: Boolean(source?.IsTitle),
    Buttons: SYSTEM_BUTTON_DEFS.map(({ key, label, flag }) => {
      const button = source?.[key as keyof GameSettingData] as AvgButton | undefined
      const buttonEx = source?.[`${key}Ex` as keyof GameSettingData] as AvgButtonIndex | undefined
      const buttonIndex = Number(buttonEx?.Index ?? 0)
      const enabled = flag ? Boolean(source?.[flag as keyof GameSettingData]) && buttonIndex > 0 : buttonIndex > 0
      return {
        key,
        label,
        enabled,
        index: buttonIndex,
        x: Number(buttonEx?.X ?? button?.X ?? 0),
        y: Number(buttonEx?.Y ?? button?.Y ?? 0),
      }
    }),
    AudioItems: SYSTEM_AUDIO_DEFS.map(({ key, label }) => ({
      key,
      label,
      enabled: Boolean(source?.[`Is${key}` as keyof GameSettingData]),
      volume: Number(source?.[`${key === 'BGM' ? 'Bgm' : key}Vol` as keyof GameSettingData] ?? 80),
      x: Number(source?.[`X_${key}` as keyof GameSettingData] ?? 0),
      y: Number(source?.[`Y_${key}` as keyof GameSettingData] ?? 0),
    })),
  }
}

function createGeneralDraft(source: TotalSetting | null | undefined): TotalSettingDraft {
  const saveFontSetting =
    source?.FontSettingList?.[0] ? cloneFontSettingDraft(source.FontSettingList[0]) : { Font: '', FontSize: 30, Height: 36 }
  return {
    FontName: source?.FontName ?? '',
    FontSize: Number(source?.FontSize ?? 30),
    IsTitle: Boolean(source?.IsTitle),
    EnterSE: cloneMusicDraft(source?.EnterSE),
    MoveSE: cloneMusicDraft(source?.MoveSE),
    CancelSE: cloneMusicDraft(source?.CancelSE),
    WarningSE: cloneMusicDraft(source?.WarningSE),
    Ico: cloneFileNameDraft(source?.Ico),
    AuthorWords: source?.AuthorWords ?? '',
    AuthorWordTime: Number(source?.AuthorWordTime ?? 0),
    Auto: Boolean(source?.Auto),
    StartMapId: Number(source?.StartMapId ?? 1),
    FontColor_MSG: source?.FontColor_MSG ?? '0, 0, 0',
    FontColor_UI: source?.FontColor_UI ?? '255, 255, 255',
    FontStyle: Number(source?.FontStyle ?? 0),
    Init_savedata: Boolean(source?.Init_savedata),
    MenuIndex: Number(source?.MenuIndex ?? 10001),
    MenuCodeSceneName: source?.MenuCodeSceneName ?? '',
    OpenVit: Boolean(source?.OpenVit),
    IsCloseCGBGM: Boolean(source?.IsCloseCGBGM),
    Show_ad: Number(source?.Show_ad ?? 1),
    FontSettingList: [saveFontSetting],
  }
}

async function openDraft() {
  settingsTab.value = 'general'
  saveLoadFontDirty.value = false
  projectNameDraft.value = project.value?.storyTree?.ProjectName ?? ''
  generalDraft.value = createGeneralDraft(project.value?.systemData?.totalSetting)
  titleDraft.value = createTitleDraft(project.value?.systemData?.Title)
  menuDraft.value = createMenuDraft(project.value?.systemData?.GameMenu)
  dialogDraft.value = createDialogDraft(project.value?.systemData?.Talk)
  replayDraft.value = createReplayDraft(project.value?.systemData?.backPlay)
  saveLoadDraft.value = createSaveLoadDraft(project.value?.systemData?.Sldata)
  systemDraft.value = createSystemDraft(project.value?.systemData?.SystemMenu)
  paymentDraft.value = createPaymentDraft(
    project.value ? ensureMallNewForProject(project.value as unknown as MainProject).GameMallNew : null,
  )
  setImageOptionButtons(createImageOptionDraft(project.value?.systemData?.btnList?.buttons))
  await syncDraftFontPreview(generalDraft.value?.FontName ?? '')
  await syncSaveLoadFontPreview(saveLoadDraft.value?.Font ?? '')
}

function resetDraft() {
  projectNameDraft.value = ''
  generalDraft.value = null
  titleDraft.value = null
  menuDraft.value = null
  dialogDraft.value = null
  replayDraft.value = null
  saveLoadDraft.value = null
  saveLoadFontDirty.value = false
  systemDraft.value = null
  paymentDraft.value = null
  resetImageOptions()
  previewFontFamily.value = ''
  saveLoadPreviewFontFamily.value = ''
  settingsTab.value = 'general'
}

function buildFileName(fileDraft: { type: number; name: string }) {
  return new FileName({
    type: fileDraft.type as never,
    name: fileDraft.name,
  })
}

function buildMusicItemDraft(itemDraft: MusicDraft) {
  return new MusicItem({
    name: buildFileName(itemDraft),
    volume: itemDraft.volume,
  })
}

function buildFontSettingDraft(fontDraft: FontSettingDraft) {
  return new FontSetting({
    f: fontDraft.Font,
    size: fontDraft.FontSize,
    h: fontDraft.Height,
  })
}

function buildMallFontDraft(fontDraft: MallFontDraft) {
  return new FontProperty(
    fontDraft.Font_name,
    Math.max(1, Math.floor(Number(fontDraft.Font_Size) || 1)),
    fontDraft.Font_color,
  )
}

function buildNameDialogDraft(draft: NameDialogDraft) {
  const next = new NameDialogData()
  next.x = draft.x
  next.y = draft.y
  next.BackPic = buildFileName(draft.BackPic)
  next.P_x = draft.P_x
  next.P_y = draft.P_y
  next.IsCenter = draft.IsCenter
  return next
}

function buildMessageDialogDraft(draft: MessageDialogDraft) {
  const next = new MessageDialogData()
  next.X = draft.X
  next.Y = draft.Y
  next.BackPic = buildFileName(draft.BackPic)
  next.FacePic = buildFileName(draft.FacePic)
  next.f_x = draft.f_x
  next.f_y = draft.f_y
  next.p_x1 = draft.p_x1
  next.p_y1 = draft.p_y1
  next.p_viewW = draft.p_viewW
  next.p_viewH = draft.p_viewH
  next.Buttons = draft.Buttons.map((item) => buildAvgButtonDraft(item))
  next.ButtonsEx = draft.ButtonsEx.map((item) => buildAvgButtonIndexDraft(item))
  next.BIndex = [...draft.BIndex]
  return next
}

function buildTalkMessageDraft(draft: TalkMessageDraft) {
  const next = new TalkMessageData()
  next.FaceStyle = draft.FaceStyle
  next.SelectButton = draft.SelectButton
  next.SelectPic = draft.SelectPic
  next.NameWinS = buildNameDialogDraft(draft.NameWinS)
  next.TalkWin = buildMessageDialogDraft(draft.TalkWin)
  return next
}

function buildAvgButtonDraft(draft: AvgButtonDraft) {
  return new AvgButton(
    draft.Name,
    buildFileName(draft.NoneImage),
    buildFileName(draft.MoveImage),
    draft.X,
    draft.Y,
  )
}

function buildViewportDraft(draft: ViewportDraft) {
  return new Viewport(draft.X, draft.Y, draft.Width, draft.Height)
}

function buildAvgButtonIndexDraft(draft: AvgButtonIndexDraft) {
  return new AvgButtonIndex(draft.Index, draft.X, draft.Y)
}

function buildGoodPropertyDraft(draft: GoodPropertyDraft) {
  const next = new GoodPropertyModel()
  next.PropertyId = Math.floor(Number(draft.PropertyId) || -1)
  next.Operation = Math.floor(Number(draft.Operation) || -1)
  next.Number = Math.floor(Number(draft.Number) || -1)
  return next
}

function buildGoodDetailPageDraft(draft: GoodDetailPageDraft) {
  const next = new GoodDetailPageModel()
  next.DetailGraph = buildFileName(draft.DetailGraph)
  next.PreviewBackGraph = buildFileName(draft.PreviewBackGraph)
  next.PreviewBack_X = Math.floor(Number(draft.PreviewBack_X) || 0)
  next.PreviewBack_Y = Math.floor(Number(draft.PreviewBack_Y) || 0)
  next.PreviewBack_W = Math.max(1, Math.floor(Number(draft.PreviewBack_W) || 1))
  next.PreviewBack_H = Math.max(1, Math.floor(Number(draft.PreviewBack_H) || 1))
  next.Preview_X = Math.floor(Number(draft.Preview_X) || 0)
  next.Preview_Y = Math.floor(Number(draft.Preview_Y) || 0)
  next.Preview_W = Math.max(1, Math.floor(Number(draft.Preview_W) || 1))
  next.Preview_H = Math.max(1, Math.floor(Number(draft.Preview_H) || 1))
  next.GoodName_X = Math.floor(Number(draft.GoodName_X) || 0)
  next.GoodName_Y = Math.floor(Number(draft.GoodName_Y) || 0)
  next.FlowerTotal_X = Math.floor(Number(draft.FlowerTotal_X) || 0)
  next.FlowerTotal_Y = Math.floor(Number(draft.FlowerTotal_Y) || 0)
  next.Flower_X = Math.floor(Number(draft.Flower_X) || 0)
  next.Flower_Y = Math.floor(Number(draft.Flower_Y) || 0)
  next.GoodPrice_X = Math.floor(Number(draft.GoodPrice_X) || 0)
  next.GoodPrice_Y = Math.floor(Number(draft.GoodPrice_Y) || 0)
  next.BuyCount_X = Math.floor(Number(draft.BuyCount_X) || 0)
  next.BuyCount_Y = Math.floor(Number(draft.BuyCount_Y) || 0)
  next.GoodIntroView = buildViewportDraft(draft.GoodIntroView)
  next.Ok_btn = buildAvgButtonIndexDraft(draft.Ok_btn)
  next.Cancel_btn = buildAvgButtonIndexDraft(draft.Cancel_btn)
  next.Add_btn = buildAvgButtonIndexDraft(draft.Add_btn)
  next.Minus_btn = buildAvgButtonIndexDraft(draft.Minus_btn)
  next.IsUseImageIndex = Boolean(draft.IsUseImageIndex)
  next.Index = Math.max(0, Math.floor(Number(draft.Index) || 0))
  next.IndexImage = buildFileName(draft.IndexImage)
  next.IsShowBuyCount = Boolean(draft.IsShowBuyCount)
  next.IsShowGoodName = Boolean(draft.IsShowGoodName)
  next.IsShowGoodIntro = Boolean(draft.IsShowGoodIntro)
  next.IsShowGoodImage = Boolean(draft.IsShowGoodImage)
  return next
}

function buildReplayDraft(draft: ReplayDraft) {
  const next = new GameReplay()
  next.BackPic = buildFileName(draft.BackPic)
  next.Viewport = buildViewportDraft(draft.Viewport)
  next.Close = buildAvgButtonDraft(draft.Close)
  next.CloseEx = buildAvgButtonIndexDraft(draft.CloseEx)
  next.ButtonIndex = draft.ButtonIndex
  return next
}

function buildSaveLoadDataDraft(
  draft: SaveLoadDraft,
  preservedFont?: Pick<SaveLoadData, 'CundangFontName' | 'CundangFontSize'> | null,
) {
  const next = new SaveLoadData()
  next.BackPic = buildFileName(draft.BackPic)
  next.Viewport = buildViewportDraft(draft.Viewport)
  next.Max = Math.max(1, Math.floor(Number(draft.Max) || 1))
  next.Column = Math.max(1, Math.floor(Number(draft.Column) || 1))
  next.SpanRow = Math.max(1, Math.floor(Number(draft.SpanRow) || 1))
  next.SpanCol = Math.max(1, Math.floor(Number(draft.SpanCol) || 1))
  next.BackButtonEx = buildAvgButtonIndexDraft(draft.BackButtonEx)
  next.CloseButtonEx = buildAvgButtonIndexDraft(draft.CloseButtonEx)
  const backSource = imageOptionDraft.value[draft.BackButtonEx.Index] ?? createReservedImageOptionButtonDraft()
  const closeSource = imageOptionDraft.value[draft.CloseButtonEx.Index] ?? createReservedImageOptionButtonDraft()
  next.BackButton = buildAvgButtonDraft(backSource)
  next.BackButton.X = draft.BackButtonEx.X
  next.BackButton.Y = draft.BackButtonEx.Y
  next.CloseButton = buildAvgButtonDraft(closeSource)
  next.CloseButton.X = draft.CloseButtonEx.X
  next.CloseButton.Y = draft.CloseButtonEx.Y
  next.IsMapName = draft.IsMapName
  next.IsDate = Boolean(draft.IsDate)
  next.IsMinPic = draft.IsMinPic
  next.Name_X = draft.Name_X
  next.Name_Y = draft.Name_Y
  next.Date_X = draft.Date_X
  next.Date_Y = draft.Date_Y
  next.Pic_X = draft.Pic_X
  next.Pic_Y = draft.Pic_Y
  next.Zoom = Math.max(1, Math.floor(Number(draft.Zoom) || 1))
  next.BtnIndex = [draft.BackButtonEx.Index, draft.CloseButtonEx.Index]
  next.IsPage = draft.IsPage ? 1 : 0
  next.PrePic = buildAvgButtonIndexDraft(draft.PrePic)
  next.NextPic = buildAvgButtonIndexDraft(draft.NextPic)
  next.PageX = draft.PageX
  next.PageY = draft.PageY
  applySaveLoadFontFields(next, draft, preservedFont ?? null)
  return next
}

function buildTitleDataDraft(draft: TitleDraft) {
  const next = new GameTitleData()
  next.IsLogo = draft.IsLogo
  next.LogoPic = buildFileName(draft.LogoPic)
  next.TitlePic = buildFileName(draft.TitlePic)
  next.TitleBGM = buildMusicItemDraft(draft.TitleBGM)
  next.IsDrawText = draft.IsDrawText
  next.IsFree = true
  next.Buttons = draft.Buttons.map((item) => {
    const source = imageOptionDraft.value[item.enabled ? item.index : 0] ?? createReservedImageOptionButtonDraft()
    const button = buildAvgButtonDraft(source)
    button.X = item.x
    button.Y = item.y
    return button
  })
  next.ButtonsEx = draft.Buttons.map((item) =>
    buildAvgButtonIndexDraft({
      Index: item.enabled ? item.index : 0,
      X: item.x,
      Y: item.y,
    }),
  )
  next.BIndex = draft.Buttons.map((item) => (item.enabled ? item.index : 0))
  return next
}

function buildMenuDataDraft(draft: MenuDraft) {
  const next = new GameMenuData()
  next.BackPic = buildFileName(draft.BackPic)
  next.IsFree = true
  next.Buttons = draft.Buttons.map((item) => {
    const source = imageOptionDraft.value[item.enabled ? item.index : 0] ?? createReservedImageOptionButtonDraft()
    const button = buildAvgButtonDraft(source)
    button.X = item.x
    button.Y = item.y
    return button
  })
  next.ButtonsEx = draft.Buttons.map((item) =>
    buildAvgButtonIndexDraft({
      Index: item.enabled ? item.index : 0,
      X: item.x,
      Y: item.y,
    }),
  )
  next.BIndex = draft.Buttons.map((item) => (item.enabled ? item.index : 0))
  return next
}

function buildSystemDataDraft(draft: SystemDraft) {
  const next = new GameSettingData()
  next.BackPic = buildFileName(draft.BackPic)
  next.BarNone = buildFileName(draft.BarNone)
  next.BarMove = buildFileName(draft.BarMove)
  next.ButtonsIndex = draft.Buttons.map((item) => (item.enabled ? item.index : 0))
  next.IsTitle = draft.IsTitle
  next.IsAutoText = draft.IsAutoText
  next.IsFullScreen = draft.IsFullScreen
  next.IsBGM = draft.IsBGM
  next.IsSE = draft.IsSE
  next.IsVoice = draft.IsVoice
  for (const button of draft.Buttons) {
    const source = imageOptionDraft.value[button.enabled ? button.index : 0] ?? createReservedImageOptionButtonDraft()
    const builtButton = buildAvgButtonDraft(source)
    builtButton.X = button.x
    builtButton.Y = button.y
    const builtEx = buildAvgButtonIndexDraft({
      Index: button.enabled ? button.index : 0,
      X: button.x,
      Y: button.y,
    })
    ;(next as unknown as Record<string, unknown>)[button.key] = builtButton
    ;(next as unknown as Record<string, unknown>)[`${button.key}Ex`] = builtEx
  }
  for (const audio of draft.AudioItems) {
    ;(next as unknown as Record<string, unknown>)[`Is${audio.key}`] = audio.enabled
    ;(next as unknown as Record<string, unknown>)[`${audio.key === 'BGM' ? 'Bgm' : audio.key}Vol`] = audio.volume
    ;(next as unknown as Record<string, unknown>)[`X_${audio.key}`] = audio.x
    ;(next as unknown as Record<string, unknown>)[`Y_${audio.key}`] = audio.y
  }
  return next
}

function commitDraft() {
  const p = project.value
  const draft = generalDraft.value
  const title = titleDraft.value
  const menu = menuDraft.value
  const talkDraft = dialogDraft.value
  const replay = replayDraft.value
  const saveLoad = saveLoadDraft.value
  const system = systemDraft.value
  const payment = paymentDraft.value
  if (!p || !draft || !title || !menu || !talkDraft || !replay || !saveLoad || !system || !payment) return

  p.storyTree.ProjectName = projectNameDraft.value

  const next = new TotalSetting()
  next.FontName = draft.FontName
  next.FontSize = draft.FontSize
  next.IsTitle = draft.IsTitle
  next.EnterSE = buildMusicItemDraft(draft.EnterSE)
  next.MoveSE = buildMusicItemDraft(draft.MoveSE)
  next.CancelSE = buildMusicItemDraft(draft.CancelSE)
  next.WarningSE = buildMusicItemDraft(draft.WarningSE)
  next.Ico = buildFileName(draft.Ico)
  next.AuthorWords = draft.AuthorWords
  next.AuthorWordTime = draft.AuthorWordTime
  next.Auto = draft.Auto
  next.StartMapId = draft.StartMapId
  next.FontColor_MSG = draft.FontColor_MSG
  next.FontColor_UI = draft.FontColor_UI
  next.FontStyle = draft.FontStyle
  next.Init_savedata = draft.Init_savedata
  next.MenuIndex = draft.MenuIndex
  next.MenuCodeSceneName = draft.MenuCodeSceneName
  next.OpenVit = draft.OpenVit
  next.IsCloseCGBGM = draft.IsCloseCGBGM
  next.Show_ad = draft.Show_ad
  next.FontSettingList = draft.FontSettingList.map((item) => buildFontSettingDraft(item))
  const nextBtnList = new BtnList()
  nextBtnList.buttons = imageOptionDraft.value.map((item) => buildAvgButtonDraft(item))
  p.systemData.totalSetting = next
  p.systemData.Sldata = buildSaveLoadDataDraft(
    saveLoad,
    saveLoadFontDirty.value ? null : p.systemData.Sldata,
  )
  p.systemData.Title = buildTitleDataDraft(title)
  p.systemData.GameMenu = buildMenuDataDraft(menu)
  p.systemData.Talk = buildTalkMessageDraft(talkDraft)
  p.systemData.backPlay = buildReplayDraft(replay)
  p.systemData.SystemMenu = buildSystemDataDraft(system)
  p.systemData.btnList = nextBtnList

  const mallInput = ensureMallNewForProject(p as unknown as MainProject)
  const mall = mallInput.GameMallNew
  const sourceGoods = [...mall.GoodsList]
  mall.MallBack = buildFileName(payment.MallBack)
  mall.DecorateBack = buildFileName(payment.DecorateBack)
  mall.Decorate_X = payment.Decorate_X
  mall.Decorate_Y = payment.Decorate_Y
  mall.GoodAreaBack = buildFileName(payment.GoodAreaBack)
  mall.GoodAreaBack_X = payment.GoodAreaBack_X
  mall.GoodAreaBack_Y = payment.GoodAreaBack_Y
  mall.GoodAreaBack_W = Math.max(1, Math.floor(Number(payment.GoodAreaBack_W) || 1))
  mall.GoodAreaBack_H = Math.max(1, Math.floor(Number(payment.GoodAreaBack_H) || 1))
  mall.ClosePic = buildAvgButtonIndexDraft(payment.ClosePic)
  mall.FlowerPic = buildFileName(payment.FlowerPic)
  mall.Flower_X = Math.floor(Number(payment.Flower_X) || 0)
  mall.Flower_Y = Math.floor(Number(payment.Flower_Y) || 0)
  mall.FlowerOwn_X = Math.floor(Number(payment.FlowerOwn_X) || 0)
  mall.FlowerOwn_Y = Math.floor(Number(payment.FlowerOwn_Y) || 0)
  mall.FlowerPrice_X = Math.floor(Number(payment.FlowerPrice_X) || 0)
  mall.FlowerPrice_Y = Math.floor(Number(payment.FlowerPrice_Y) || 0)
  mall.PrePic = buildAvgButtonIndexDraft(payment.PrePic)
  mall.NextPic = buildAvgButtonIndexDraft(payment.NextPic)
  mall.PageX = payment.PageX
  mall.PageY = payment.PageY
  mall.TagButton = buildAvgButtonIndexDraft(payment.TagButton)
  mall.TagName = payment.TagName
  mall.IsShowGoodName = payment.IsShowGoodName
  mall.IsShowGoodIntro = payment.IsShowGoodIntro
  mall.IsShowGoodImage = payment.IsShowGoodImage
  mall.IsShowOneKeyGetBtn = payment.IsShowOneKeyGetBtn
  mall.OneKeyGetBtn = buildAvgButtonIndexDraft(payment.OneKeyGetBtn)
  mall.GoodDetailPage = buildGoodDetailPageDraft(payment.GoodDetailPage)
  mall.GoodsList = payment.GoodsList.map((item, index) => {
    const nextGoods = sourceGoods[index] ?? new Goods()
    nextGoods.GoodsId = Math.max(0, Math.floor(Number(item.GoodsId) || index + 1))
    nextGoods.GoodsName = item.GoodsName
    nextGoods.GoodsFont = buildMallFontDraft(item.GoodsFont)
    nextGoods.GoodsName_X = Math.floor(Number(item.GoodsName_X) || 0)
    nextGoods.GoodsName_Y = Math.floor(Number(item.GoodsName_Y) || 0)
    nextGoods.GoodsType = Math.max(1, Math.floor(Number(item.GoodsType) || 1))
    nextGoods.GoodsPrice = Math.max(0, Math.floor(Number(item.GoodsPrice) || 0))
    nextGoods.GoodsPricePakoras = Math.max(0, Math.floor(Number(item.GoodsPricePakoras) || 0))
    nextGoods.GoodsPriceFont = buildMallFontDraft(item.GoodsPriceFont)
    nextGoods.GoodsPrice_X = Math.floor(Number(item.GoodsPrice_X) || 0)
    nextGoods.GoodsPrice_Y = Math.floor(Number(item.GoodsPrice_Y) || 0)
    nextGoods.GoodsIntro = item.GoodsIntro
    nextGoods.GoodsIntroFont = buildMallFontDraft(item.GoodsIntroFont)
    nextGoods.GoodIntroView = buildViewportDraft(item.GoodIntroView)
    nextGoods.Flower_X = Math.floor(Number(item.Flower_X) || 0)
    nextGoods.Flower_Y = Math.floor(Number(item.Flower_Y) || 0)
    nextGoods.Good_Status = Math.max(1, Math.floor(Number(item.Good_Status) || 1))
    nextGoods.CanBuyRepeat = nextGoods.GoodsType === GoodsTypeEnum.Var && item.CanBuyRepeat
    nextGoods.GoodBack = buildFileName(item.GoodBack)
    nextGoods.GoodsBack_X = Math.floor(Number(item.GoodsBack_X) || 0)
    nextGoods.GoodsBack_Y = Math.floor(Number(item.GoodsBack_Y) || 0)
    nextGoods.GoodsBack_W = Math.max(1, Math.floor(Number(item.GoodsBack_W) || 1))
    nextGoods.GoodsBack_H = Math.max(1, Math.floor(Number(item.GoodsBack_H) || 1))
    nextGoods.Preview = buildFileName(item.Preview)
    nextGoods.Preview_X = Math.floor(Number(item.Preview_X) || 0)
    nextGoods.Preview_Y = Math.floor(Number(item.Preview_Y) || 0)
    nextGoods.Preview_W = Math.max(1, Math.floor(Number(item.Preview_W) || 1))
    nextGoods.Preview_H = Math.max(1, Math.floor(Number(item.Preview_H) || 1))
    nextGoods.PreviewBack = buildFileName(item.PreviewBack)
    nextGoods.PreviewBack_X = Math.floor(Number(item.PreviewBack_X) || 0)
    nextGoods.PreviewBack_Y = Math.floor(Number(item.PreviewBack_Y) || 0)
    nextGoods.PreviewBack_W = Math.max(1, Math.floor(Number(item.PreviewBack_W) || 1))
    nextGoods.PreviewBack_H = Math.max(1, Math.floor(Number(item.PreviewBack_H) || 1))
    nextGoods.BuyBtn = buildAvgButtonIndexDraft(item.BuyBtn)
    nextGoods.BoughtBtn = buildFileName(item.BoughtBtn)
    nextGoods.NoUpPic = buildFileName(item.NoUpPic)
    nextGoods.PropertyList = (item.PropertyList ?? []).map((property) => buildGoodPropertyDraft(property))
    return nextGoods
  })
}

function closeWithCancel() {
  resetDraft()
  emit('update:show', false)
}

function applyChanges(): boolean {
  if (hasInvalidProjectNameChar(projectNameDraft.value)) {
    message.warning(PROJECT_NAME_INVALID_CHAR_MESSAGE)
    return false
  }
  if (!isProjectNameLengthValid(projectNameDraft.value)) {
    message.warning(PROJECT_NAME_LIMIT_MESSAGE)
    return false
  }
  commitDraft()
  workspace.syncProjectDisplayName()
  editor.touchSceneEvents()
  void ensureProjectFontLoaded(project.value as MainProject | null, workspace.virtualResourceRoots.font)
  return true
}

function closeWithConfirm() {
  if (!applyChanges()) return
  resetDraft()
  emit('update:show', false)
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      void refreshSettingsPortraitChromeMetrics()
      void openDraft().then(() => refreshSettingsPortraitChromeMetrics())
      return
    }
    resetDraft()
  },
)

const previewFont = computed(() => {
  if (previewFontFamily.value) {
    return `"${previewFontFamily.value}", "Microsoft YaHei", sans-serif`
  }
  return '"Microsoft YaHei", sans-serif'
})
const previewSize = computed(() => {
  return `${Math.max(12, Number(totalSetting.value?.FontSize ?? 30))}px`
})
const previewMessageColor = computed(() => totalSetting.value?.FontColor_MSG || '0, 0, 0')
const previewUiColor = computed(() => totalSetting.value?.FontColor_UI || '255, 255, 255')
const messageColorHex = computed({
  get: () => rgbStringToHex(totalSetting.value?.FontColor_MSG ?? '0, 0, 0', '#000000'),
  set: (value: string) => {
    if (!totalSetting.value) return
    totalSetting.value.FontColor_MSG = hexToRgbString(value)
  },
})
const uiColorHex = computed({
  get: () => rgbStringToHex(totalSetting.value?.FontColor_UI ?? '255, 255, 255', '#ffffff'),
  set: (value: string) => {
    if (!totalSetting.value) return
    totalSetting.value.FontColor_UI = hexToRgbString(value)
  },
})
async function syncDraftFontPreview(storedPath: string) {
  const token = ++defaultFontPreviewToken
  if (!generalDraft.value) {
    return
  }
  try {
    const result = await ensureFontLoadedFromVirtualResource({
      root: workspace.virtualResourceRoots.font,
      storedPath,
    })
    if (token !== defaultFontPreviewToken || !generalDraft.value) return
    generalDraft.value.FontName = result.storedPath
    previewFontFamily.value = result.family
  } catch {
    if (token !== defaultFontPreviewToken || !generalDraft.value) return
    previewFontFamily.value = ''
  }
}

async function syncSaveLoadFontPreview(storedPath: string) {
  const token = ++saveLoadFontPreviewToken
  saveLoadPreviewFontFamily.value = ''
  if (!saveLoadDraft.value) {
    return
  }
  try {
    const result = await ensureFontLoadedFromVirtualResource({
      root: workspace.virtualResourceRoots.font,
      storedPath,
    })
    if (token !== saveLoadFontPreviewToken || !saveLoadDraft.value) return
    saveLoadDraft.value.Font = result.storedPath
    saveLoadPreviewFontFamily.value = result.family
  } catch {
    if (token !== saveLoadFontPreviewToken || !saveLoadDraft.value) return
    saveLoadPreviewFontFamily.value = ''
  }
}

function resolveGraphicPreviewUrl(storedPath: string, baseFolderPath?: string): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    baseFolderPath,
  )
}

const previewNameBoxUrl = computed(() =>
  resolveGraphicPreviewUrl(talkSetting.value?.NameWinS.BackPic.name ?? '', DIALOG_GRAPHIC_BASE_FOLDER),
)
const previewTalkBoxUrl = computed(() =>
  resolveGraphicPreviewUrl(talkSetting.value?.TalkWin.BackPic.name ?? '', DIALOG_GRAPHIC_BASE_FOLDER),
)
const previewMessageColorCss = computed(() => `rgb(${previewMessageColor.value})`)
const previewUiColorCss = computed(() => `rgb(${previewUiColor.value})`)
const previewNameBoxSourceWidth = computed(() => previewNameImageSize.value.width || 180)
const previewNameBoxSourceHeight = computed(() => previewNameImageSize.value.height || 60)
const previewTalkBoxSourceWidth = computed(() => previewTalkImageSize.value.width || 720)
const previewTalkBoxSourceHeight = computed(() => previewTalkImageSize.value.height || 180)
const previewTalkBoxLeft = computed(() => Number(talkSetting.value?.TalkWin.X ?? 0))
const previewTalkBoxTop = computed(() => {
  return projectScreenHeight.value - Number(talkSetting.value?.TalkWin.Y ?? 0) - previewTalkBoxSourceHeight.value
})
const previewNameBoxLeft = computed(() => previewTalkBoxLeft.value + Number(talkSetting.value?.NameWinS.x ?? 0))
const previewNameBoxTop = computed(() => previewTalkBoxTop.value + Number(talkSetting.value?.NameWinS.y ?? 0))
const previewNameTextFontSize = computed(() => Number(totalSetting.value?.FontSize ?? 30))
const previewNameTextMetrics = computed(() => {
  return measureBitmapFontText('姓名', previewNameTextFontSize.value, previewFont.value)
})
const previewCenteredNameTextOffsetX = computed(() =>
  getPreviewCenterOffset(previewNameBoxSourceWidth.value, previewNameTextMetrics.value.width),
)
const previewCenteredNameTextOffsetY = computed(() =>
  getPreviewCenterOffset(previewNameBoxSourceHeight.value, previewNameTextMetrics.value.height),
)
const previewNameTextLeft = computed(() =>
  Boolean(talkSetting.value?.NameWinS.IsCenter)
    ? previewNameBoxLeft.value + previewCenteredNameTextOffsetX.value
    : previewNameBoxLeft.value + Number(talkSetting.value?.NameWinS.P_x ?? 0),
)
const previewNameTextTop = computed(() =>
  Boolean(talkSetting.value?.NameWinS.IsCenter)
    ? previewNameBoxTop.value + previewCenteredNameTextOffsetY.value
    : previewNameBoxTop.value + Number(talkSetting.value?.NameWinS.P_y ?? 0),
)
const previewNameTextWidth = computed(() =>
  Boolean(talkSetting.value?.NameWinS.IsCenter)
    ? previewNameTextMetrics.value.width
    : Math.max(24, previewNameBoxSourceWidth.value - Number(talkSetting.value?.NameWinS.P_x ?? 0)),
)
const previewNameTextHeight = computed(() =>
  Boolean(talkSetting.value?.NameWinS.IsCenter)
    ? previewNameTextMetrics.value.height
    : Math.max(18, previewNameBoxSourceHeight.value - Number(talkSetting.value?.NameWinS.P_y ?? 0)),
)
const cachedManualNameTextOffset = ref<{ x: number; y: number } | null>(null)

watch(
  [
    () => Boolean(talkSetting.value?.NameWinS.IsCenter),
    previewCenteredNameTextOffsetX,
    previewCenteredNameTextOffsetY,
  ],
  ([isCenter, centeredX, centeredY], previousValue) => {
    const draft = talkSetting.value?.NameWinS
    const wasCenter = previousValue?.[0] ?? false
    if (!draft) return
    if (isCenter) {
      if (!wasCenter) {
        cachedManualNameTextOffset.value = {
          x: Number(draft.P_x ?? 0),
          y: Number(draft.P_y ?? 0),
        }
      }
      if (draft.P_x !== centeredX) {
        draft.P_x = centeredX
      }
      if (draft.P_y !== centeredY) {
        draft.P_y = centeredY
      }
      return
    }
    if (wasCenter && cachedManualNameTextOffset.value) {
      draft.P_x = cachedManualNameTextOffset.value.x
      draft.P_y = cachedManualNameTextOffset.value.y
      cachedManualNameTextOffset.value = null
    }
  },
  { immediate: true },
)

const previewNameTextStyle = computed<CSSProperties>(() => ({
  left: `${previewNameTextLeft.value}px`,
  top: `${previewNameTextTop.value}px`,
  width: `${previewNameTextWidth.value}px`,
  height: `${previewNameTextHeight.value}px`,
  color: previewMessageColorCss.value,
  fontFamily: previewFont.value,
  fontSize: `${previewNameTextFontSize.value}px`,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  textAlign: 'left',
}))
const previewNameTextHandleStyle = computed(() => ({
  left: `${previewNameTextLeft.value + previewNameTextWidth.value - 6}px`,
  top: `${previewNameTextTop.value - 6}px`,
}))
const previewNameBoxStyle = computed(() => ({
  left: `${previewNameBoxLeft.value}px`,
  top: `${previewNameBoxTop.value}px`,
  width: `${previewNameBoxSourceWidth.value}px`,
  height: `${previewNameBoxSourceHeight.value}px`,
}))
const previewTalkBoxStyle = computed(() => ({
  left: `${previewTalkBoxLeft.value}px`,
  top: `${previewTalkBoxTop.value}px`,
  width: `${previewTalkBoxSourceWidth.value}px`,
  height: `${previewTalkBoxSourceHeight.value}px`,
}))
const previewTalkTextRegionStyle = computed(() => ({
  left: `${previewTalkBoxLeft.value + Number(talkSetting.value?.TalkWin.p_x1 ?? 0)}px`,
  top: `${previewTalkBoxTop.value + Number(talkSetting.value?.TalkWin.p_y1 ?? 0)}px`,
  width: `${talkSetting.value?.TalkWin.p_viewW ?? 0}px`,
  height: `${talkSetting.value?.TalkWin.p_viewH ?? 0}px`,
  color: previewMessageColorCss.value,
  fontFamily: previewFont.value,
  fontSize: `${Number(totalSetting.value?.FontSize ?? 30)}px`,
}))

const previewSampleContent = computed(() => '这里是对话内容预览。\n第二行文字会显示在文本区域内。')
const replayBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(replaySetting.value?.BackPic.name ?? '', SETTINGS_BACKGROUND_BASE_FOLDER),
)
const titleBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(titleSetting.value?.TitlePic.name ?? '', TITLE_BACKGROUND_BASE_FOLDER),
)
const titlePreviewButtons = computed(() => {
  return (titleSetting.value?.Buttons ?? [])
    .map((item, index) => {
      if (!item.enabled || item.index <= 0) return null
      const imageOption = imageOptionButtons.value[item.index]
      const url =
        resolveGraphicPreviewUrl(imageOption?.NoneImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER) ||
        resolveGraphicPreviewUrl(imageOption?.MoveImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER)
      const size = previewTitleButtonImageSizes.value[url] ?? { width: 72, height: 32 }
      return {
        key: index,
        label: item.label,
        url,
        style: {
          left: `${item.x}px`,
          top: `${item.y}px`,
          width: `${Math.max(1, size.width)}px`,
          height: `${Math.max(1, size.height)}px`,
        },
      }
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
})
const menuBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(menuDraft.value?.BackPic.name ?? '', SETTINGS_BACKGROUND_BASE_FOLDER),
)
const menuPreviewButtons = computed(() => {
  return (menuDraft.value?.Buttons ?? [])
    .map((item, index) => {
      if (!item.enabled || item.index <= 0) return null
      const imageOption = imageOptionButtons.value[item.index]
      const url =
        resolveGraphicPreviewUrl(imageOption?.NoneImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER) ||
        resolveGraphicPreviewUrl(imageOption?.MoveImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER)
      const size = previewTitleButtonImageSizes.value[url] ?? { width: 72, height: 32 }
      return {
        key: index,
        label: item.label,
        url,
        style: {
          left: `${item.x}px`,
          top: `${item.y}px`,
          width: `${Math.max(1, size.width)}px`,
          height: `${Math.max(1, size.height)}px`,
        },
      }
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
})
const systemBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(systemDraft.value?.BackPic.name ?? '', SETTINGS_BACKGROUND_BASE_FOLDER),
)
const systemBarNoneUrl = computed(() =>
  resolveGraphicPreviewUrl(systemDraft.value?.BarNone.name ?? '', DIALOG_GRAPHIC_BASE_FOLDER),
)
const systemBarMoveUrl = computed(() =>
  resolveGraphicPreviewUrl(systemDraft.value?.BarMove.name ?? '', DIALOG_GRAPHIC_BASE_FOLDER),
)
const systemPreviewButtons = computed(() => {
  return (systemDraft.value?.Buttons ?? [])
    .map((item, index) => {
      if (!item.enabled || item.index <= 0) return null
      const imageOption = imageOptionButtons.value[item.index]
      const url =
        resolveGraphicPreviewUrl(imageOption?.NoneImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER) ||
        resolveGraphicPreviewUrl(imageOption?.MoveImage.name ?? '', IMAGE_OPTION_GRAPHIC_BASE_FOLDER)
      const size = previewTitleButtonImageSizes.value[url] ?? { width: 72, height: 32 }
      return {
        key: index,
        label: item.label,
        url,
        style: {
          left: `${item.x}px`,
          top: `${item.y}px`,
          width: `${Math.max(1, size.width)}px`,
          height: `${Math.max(1, size.height)}px`,
        },
      }
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
})
const systemPreviewAudioItems = computed(() => {
  const trackWidth = previewSystemBarNoneImageSize.value.width || 160
  const trackHeight = previewSystemBarNoneImageSize.value.height || 16
  const thumbWidth = previewSystemBarMoveImageSize.value.width || 24
  const thumbHeight = previewSystemBarMoveImageSize.value.height || 18
  return (systemDraft.value?.AudioItems ?? [])
    .filter((item) => item.enabled)
    .map((item) => {
      const top = item.y
      const left = item.x
      const scaledTrackWidth = Math.max(48, trackWidth)
      const scaledTrackHeight = Math.max(8, trackHeight)
      const scaledThumbWidth = Math.max(10, thumbWidth)
      const scaledThumbHeight = Math.max(10, thumbHeight)
      const thumbOffset = Math.round((Math.max(0, Math.min(100, item.volume)) / 100) * Math.max(0, scaledTrackWidth - scaledThumbWidth))
      return {
        key: item.key,
        label: item.label,
        value: `${Math.max(0, Math.min(100, item.volume))}%`,
        labelStyle: {
          left: `${left}px`,
          top: `${top}px`,
        },
        trackStyle: {
          left: `${left + 58}px`,
          top: `${top}px`,
          width: `${scaledTrackWidth}px`,
          height: `${scaledTrackHeight}px`,
        },
        thumbStyle: {
          left: `${left + 58 + thumbOffset}px`,
          top: `${top + Math.round((scaledTrackHeight - scaledThumbHeight) / 2)}px`,
          width: `${scaledThumbWidth}px`,
          height: `${scaledThumbHeight}px`,
        },
        valueStyle: {
          left: `${left + 58 + trackWidth + 12}px`,
          top: `${top}px`,
        },
      }
    })
})
const replayCloseButtonSource = computed(() => {
  const index = replaySetting.value?.ButtonIndex ?? -1
  return imageOptionButtons.value[index] ?? null
})
const replayCloseButtonUrl = computed(() => {
  const item = replayCloseButtonSource.value
  if (!item) return ''
  return (
    resolveGraphicPreviewUrl(item.NoneImage.name, IMAGE_OPTION_GRAPHIC_BASE_FOLDER) ||
    resolveGraphicPreviewUrl(item.MoveImage.name, IMAGE_OPTION_GRAPHIC_BASE_FOLDER)
  )
})
const replayPreviewViewportStyle = computed(() => ({
  left: `${replaySetting.value?.Viewport.X ?? 0}px`,
  top: `${replaySetting.value?.Viewport.Y ?? 0}px`,
  width: `${Math.max(48, replaySetting.value?.Viewport.Width ?? 0)}px`,
  height: `${Math.max(32, replaySetting.value?.Viewport.Height ?? 0)}px`,
}))
const replayPreviewCloseStyle = computed(() => ({
  left: `${replaySetting.value?.CloseEx.X ?? 0}px`,
  top: `${replaySetting.value?.CloseEx.Y ?? 0}px`,
  width: `${Math.max(28, previewReplayCloseImageSize.value.width || 72)}px`,
  height: `${Math.max(20, previewReplayCloseImageSize.value.height || 32)}px`,
}))
const saveLoadBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(saveLoadSetting.value?.BackPic.name ?? '', DIALOG_GRAPHIC_BASE_FOLDER),
)
function collectPreviewFileUrls(root: DataFileMap | null | undefined): string[] {
  if (!root) return []
  const out: string[] = []
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    const url = workspace.resourcePreviewOverrides[node.resourceUrl] ?? node.resourceUrl
    if (url) out.push(url)
  }
  return out
}
const saveLoadPreviewThumbUrlSeed = Math.random()
const saveLoadPreviewThumbUrl = computed(() => {
  const graphicRoot = workspace.virtualResourceRoots.graphic
  const backgroundRoot = graphicRoot?.findFolderByRelativePath(
    normalizeResourceBaseFolderPath('graphic', TITLE_BACKGROUND_BASE_FOLDER),
  ) ?? null
  const candidates = collectPreviewFileUrls(backgroundRoot)
  if (!candidates.length) return ''
  const index = Math.floor(saveLoadPreviewThumbUrlSeed * candidates.length) % candidates.length
  return candidates[index] ?? ''
})
const saveLoadBackButtonUrl = computed(() => resolveImageOptionPreviewUrl(saveLoadSetting.value?.BackButtonEx.Index ?? 0))
const saveLoadCloseButtonUrl = computed(() => resolveImageOptionPreviewUrl(saveLoadSetting.value?.CloseButtonEx.Index ?? 0))
const saveLoadPrevButtonUrl = computed(() => resolveImageOptionPreviewUrl(saveLoadSetting.value?.PrePic.Index ?? 0))
const saveLoadNextButtonUrl = computed(() => resolveImageOptionPreviewUrl(saveLoadSetting.value?.NextPic.Index ?? 0))
const paymentBackgroundUrl = computed(() =>
  resolveGraphicPreviewUrl(paymentSetting.value?.MallBack.name ?? '', PAYMENT_GRAPHIC_BASE_FOLDER),
)
const paymentDecorateUrl = computed(() =>
  resolveGraphicPreviewUrl(paymentSetting.value?.DecorateBack.name ?? '', PAYMENT_GRAPHIC_BASE_FOLDER),
)
const paymentGoodAreaBackUrl = computed(() =>
  resolveGraphicPreviewUrl(paymentSetting.value?.GoodAreaBack.name ?? '', PAYMENT_GRAPHIC_BASE_FOLDER),
)
const paymentCloseButtonUrl = computed(() => resolveImageOptionPreviewUrl(paymentSetting.value?.ClosePic.Index ?? 0))
const paymentPrevButtonUrl = computed(() => resolveImageOptionPreviewUrl(paymentSetting.value?.PrePic.Index ?? 0))
const paymentNextButtonUrl = computed(() => resolveImageOptionPreviewUrl(paymentSetting.value?.NextPic.Index ?? 0))
const paymentTagButtonUrl = computed(() => resolveImageOptionPreviewUrl(paymentSetting.value?.TagButton.Index ?? 0))
const paymentOneKeyGetButtonUrl = computed(() => resolveImageOptionPreviewUrl(paymentSetting.value?.OneKeyGetBtn.Index ?? 0))
function resolvePreviewButtonSize(url: string, fallbackWidth = 72, fallbackHeight = 32) {
  const size = previewTitleButtonImageSizes.value[url] ?? { width: fallbackWidth, height: fallbackHeight }
  return {
    width: Math.max(1, size.width || fallbackWidth),
    height: Math.max(1, size.height || fallbackHeight),
  }
}
const saveLoadSlotBaseSize = computed(() => resolvePreviewButtonSize(saveLoadBackButtonUrl.value, 220, 96))
const paymentCloseButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(paymentCloseButtonUrl.value)
  return {
    left: `${paymentSetting.value?.ClosePic.X ?? 0}px`,
    top: `${paymentSetting.value?.ClosePic.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const paymentPrevButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(paymentPrevButtonUrl.value)
  return {
    left: `${paymentSetting.value?.PrePic.X ?? 0}px`,
    top: `${paymentSetting.value?.PrePic.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const paymentNextButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(paymentNextButtonUrl.value)
  return {
    left: `${paymentSetting.value?.NextPic.X ?? 0}px`,
    top: `${paymentSetting.value?.NextPic.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const paymentTagButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(paymentTagButtonUrl.value, 120, 36)
  return {
    left: `${paymentSetting.value?.TagButton.X ?? 0}px`,
    top: `${paymentSetting.value?.TagButton.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const paymentOneKeyGetButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(paymentOneKeyGetButtonUrl.value, 120, 36)
  return {
    left: `${paymentSetting.value?.OneKeyGetBtn.X ?? 0}px`,
    top: `${paymentSetting.value?.OneKeyGetBtn.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const paymentGoodAreaStyle = computed(() => ({
  left: `${paymentSetting.value?.GoodAreaBack_X ?? 0}px`,
  top: `${paymentSetting.value?.GoodAreaBack_Y ?? 0}px`,
  width: `${Math.max(1, paymentSetting.value?.GoodAreaBack_W || paymentGoodAreaImageSize.value.width || 1)}px`,
  height: `${Math.max(1, paymentSetting.value?.GoodAreaBack_H || paymentGoodAreaImageSize.value.height || 1)}px`,
}))
const paymentDecorateStyle = computed(() => ({
  left: `${paymentSetting.value?.Decorate_X ?? 0}px`,
  top: `${paymentSetting.value?.Decorate_Y ?? 0}px`,
  width: `${Math.max(1, paymentDecorateImageSize.value.width || 180)}px`,
  height: `${Math.max(1, paymentDecorateImageSize.value.height || 96)}px`,
}))
const saveLoadBackButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(saveLoadBackButtonUrl.value)
  return {
    left: `${saveLoadSetting.value?.BackButtonEx.X ?? 0}px`,
    top: `${saveLoadSetting.value?.BackButtonEx.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const saveLoadCloseButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(saveLoadCloseButtonUrl.value)
  return {
    left: `${saveLoadSetting.value?.CloseButtonEx.X ?? 0}px`,
    top: `${saveLoadSetting.value?.CloseButtonEx.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const saveLoadPrevButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(saveLoadPrevButtonUrl.value)
  return {
    left: `${saveLoadSetting.value?.PrePic.X ?? 0}px`,
    top: `${saveLoadSetting.value?.PrePic.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const saveLoadNextButtonStyle = computed(() => {
  const size = resolvePreviewButtonSize(saveLoadNextButtonUrl.value)
  return {
    left: `${saveLoadSetting.value?.NextPic.X ?? 0}px`,
    top: `${saveLoadSetting.value?.NextPic.Y ?? 0}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  }
})
const saveLoadPreviewViewportStyle = computed(() => ({
  left: `${saveLoadSetting.value?.Viewport.X ?? 0}px`,
  top: `${saveLoadSetting.value?.Viewport.Y ?? 0}px`,
  width: `${Math.max(48, saveLoadSetting.value?.Viewport.Width ?? 0)}px`,
  height: `${Math.max(32, saveLoadSetting.value?.Viewport.Height ?? 0)}px`,
}))
const saveLoadPreviewPageTextStyle = computed(() => ({
  left: `${saveLoadSetting.value?.PageX ?? 0}px`,
  top: `${saveLoadSetting.value?.PageY ?? 0}px`,
}))
const saveLoadPreviewFont = computed(() => {
  if (saveLoadPreviewFontFamily.value) {
    return `"${saveLoadPreviewFontFamily.value}", "Microsoft YaHei", sans-serif`
  }
  return previewFont.value
})
/** 预览用字号：工程未单独配置存读档字号时跟随剧情主字号。 */
const saveLoadPreviewFontSize = computed(() =>
  resolveSaveLoadPreviewFontSize(
    Number(saveLoadSetting.value?.FontSize ?? 0),
    Number(generalDraft.value?.FontSize ?? 30),
  ),
)
/** 字号步进器展示值：未配置时显示剧情字号，不写回 draft。 */
const saveLoadStepperDisplaySize = computed(() =>
  resolveSaveLoadStepperDisplaySize(
    Number(saveLoadSetting.value?.FontSize ?? 0),
    saveLoadPreviewFontSize.value,
  ),
)
function countVisibleSaveLoadSlots(areaSize: number, slotSize: number, slotStep: number): number {
  if (areaSize <= 0 || slotSize <= 0 || slotStep <= 0) return 1
  return Math.max(1, Math.floor((areaSize - slotSize) / slotStep) + 1)
}

const saveLoadPreviewPageInfo = computed(() => {
  const viewport = saveLoadSetting.value?.Viewport
  const maxCount = Math.max(1, Math.floor(Number(saveLoadSetting.value?.Max ?? 1)))
  if (!viewport) {
    return {
      pageCount: 1,
      pageCapacity: maxCount,
      columnCount: 1,
      visibleCount: maxCount,
    }
  }
  const columnLimit = Math.max(1, Math.floor(Number(saveLoadSetting.value?.Column ?? 1)))
  const slotStepX = Math.max(32, Number(saveLoadSetting.value?.SpanCol ?? 1))
  const slotStepY = Math.max(32, Number(saveLoadSetting.value?.SpanRow ?? 1))
  const slotWidth = saveLoadSlotBaseSize.value.width
  const slotHeight = saveLoadSlotBaseSize.value.height
  const columnCount = Math.min(
    columnLimit,
    countVisibleSaveLoadSlots(Number(viewport.Width ?? 0), slotWidth, slotStepX),
  )
  const rowCount = countVisibleSaveLoadSlots(Number(viewport.Height ?? 0), slotHeight, slotStepY)
  const pageCapacity = Math.max(1, columnCount * rowCount)
  const pageCount = Boolean(saveLoadSetting.value?.IsPage)
    ? Math.max(1, Math.ceil(maxCount / pageCapacity))
    : 1
  return {
    pageCount,
    pageCapacity,
    columnCount,
    visibleCount: Math.min(maxCount, pageCapacity),
  }
})

const saveLoadPreviewPageText = computed(() => `1/${saveLoadPreviewPageInfo.value.pageCount}`)

const saveLoadPreviewSlots = computed(() => {
  const viewport = saveLoadSetting.value?.Viewport
  if (!viewport) return []
  const columnCount = saveLoadPreviewPageInfo.value.columnCount
  const visibleCount = saveLoadPreviewPageInfo.value.visibleCount
  const slotStepX = Math.max(32, Number(saveLoadSetting.value?.SpanCol ?? 1))
  const slotStepY = Math.max(32, Number(saveLoadSetting.value?.SpanRow ?? 1))
  const slotWidth = saveLoadSlotBaseSize.value.width
  const slotHeight = saveLoadSlotBaseSize.value.height
  return Array.from({ length: visibleCount }, (_, index) => {
    const column = index % columnCount
    const row = Math.floor(index / columnCount)
    return {
      key: index,
      x: viewport.X + (column * slotStepX),
      y: viewport.Y + (row * slotStepY),
      width: slotWidth,
      height: slotHeight,
      title: `剧情${index + 1}`,
      date: `2016/06/0${(index % 6) + 1} 06:06`,
    }
  })
})

function updateTitleBackgroundPic(value: string) {
  if (!titleSetting.value) return
  titleSetting.value.TitlePic.name = value
}

function updateTitleBgmPath(value: string) {
  if (!titleSetting.value) return
  titleSetting.value.TitleBGM.name = value
}

function updateMenuBackPic(value: string) {
  if (!menuDraft.value) return
  menuDraft.value.BackPic.name = value
}

function updateSystemBackPic(value: string) {
  if (!systemDraft.value) return
  systemDraft.value.BackPic.name = value
}

function updateSystemBarNonePic(value: string) {
  if (!systemDraft.value) return
  systemDraft.value.BarNone.name = value
}

function updateSystemBarMovePic(value: string) {
  if (!systemDraft.value) return
  systemDraft.value.BarMove.name = value
}

function applyReplayButtonIndex(index: number) {
  const replay = replaySetting.value
  if (!replay) return
  replay.ButtonIndex = index
  replay.CloseEx.Index = index
  const item = imageOptionButtons.value[index]
  if (!item) return
  replay.Close = cloneAvgButtonDraft(buildAvgButtonDraft(item))
  replay.CloseEx.X = item.X
  replay.CloseEx.Y = item.Y
}

function updateReplayBackPic(value: string) {
  if (!replaySetting.value) return
  replaySetting.value.BackPic.name = value
}

function updateReplayButtonIndex(value: number) {
  applyReplayButtonIndex(value)
}

async function loadPreviewImageSize(url: string) {
  const target = String(url || '').trim()
  if (!target) return { width: 0, height: 0 }
  try {
    const image = await getCachedImageResource(target)
    return {
      width: image.naturalWidth || image.width || 0,
      height: image.naturalHeight || image.height || 0,
    }
  } catch {
    return { width: 0, height: 0 }
  }
}

type PreviewDragState = {
  target: 'name-box' | 'talk-box' | 'name-text' | 'talk-text'
  startX: number
  startY: number
  originX: number
  originY: number
}

const previewDragState = ref<PreviewDragState | null>(null)
const previewResizeState = ref<{
  startX: number
  startY: number
  originW: number
  originH: number
} | null>(null)
const replayPreviewDragState = ref<{
  target: 'viewport' | 'close'
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const replayPreviewResizeState = ref<{
  startX: number
  startY: number
  originW: number
  originH: number
} | null>(null)
const saveLoadPreviewDragState = ref<{
  target: 'viewport' | 'name' | 'date' | 'thumb' | 'close' | 'prev' | 'next' | 'page'
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const saveLoadPreviewResizeState = ref<{
  startX: number
  startY: number
  originW: number
  originH: number
} | null>(null)
const titlePreviewDragState = ref<{
  index: number
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const menuPreviewDragState = ref<{
  index: number
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const systemPreviewDragState = ref<{
  index: number
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const paymentPreviewDragState = ref<{
  target:
    | 'decorate'
    | 'area'
    | 'close'
    | 'prev'
    | 'next'
    | 'tag'
    | 'onekey'
    | 'page'
    | 'flower'
    | 'flower-own'
    | 'flower-price'
    | 'goods-back'
    | 'goods-icon'
    | 'goods-preview-back'
    | 'goods-name'
    | 'goods-intro'
    | 'goods-flower'
    | 'goods-price'
    | 'goods-status'
  goodsIndex?: number
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const paymentPreviewResizeState = ref<{
  target: 'area' | 'goods-back' | 'goods-icon' | 'goods-preview-back' | 'goods-intro'
  goodsIndex?: number
  startX: number
  startY: number
  originW: number
  originH: number
} | null>(null)

function handlePreviewPointerMove(event: PointerEvent) {
  const state = previewDragState.value
  const draft = talkSetting.value
  if (!state || !draft) return
  const scale = stageScales.dialog.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  if (state.target === 'name-box') {
    draft.NameWinS.x = state.originX + dx
    draft.NameWinS.y = state.originY + dy
    return
  }
  if (state.target === 'name-text') {
    draft.NameWinS.P_x = state.originX + dx
    draft.NameWinS.P_y = state.originY + dy
    return
  }
  if (state.target === 'talk-text') {
    draft.TalkWin.p_x1 = state.originX + dx
    draft.TalkWin.p_y1 = state.originY + dy
    return
  }
  draft.TalkWin.X = state.originX + dx
  draft.TalkWin.Y = state.originY - dy
}

function handlePreviewResizeMove(event: PointerEvent) {
  const state = previewResizeState.value
  const draft = talkSetting.value
  if (!state || !draft) return
  const scale = stageScales.dialog.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  draft.TalkWin.p_viewW = Math.max(1, state.originW + dx)
  draft.TalkWin.p_viewH = Math.max(1, state.originH + dy)
}

function handleReplayPreviewMove(event: PointerEvent) {
  const state = replayPreviewDragState.value
  const draft = replaySetting.value
  if (!state || !draft) return
  const scale = stageScales.replay.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  if (state.target === 'close') {
    draft.CloseEx.X = state.originX + dx
    draft.CloseEx.Y = state.originY + dy
    return
  }
  draft.Viewport.X = state.originX + dx
  draft.Viewport.Y = state.originY + dy
}

function handleReplayPreviewResizeMove(event: PointerEvent) {
  const state = replayPreviewResizeState.value
  const draft = replaySetting.value
  if (!state || !draft) return
  const scale = stageScales.replay.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  draft.Viewport.Width = Math.max(1, state.originW + dx)
  draft.Viewport.Height = Math.max(1, state.originH + dy)
}

function handleSaveLoadPreviewMove(event: PointerEvent) {
  const state = saveLoadPreviewDragState.value
  const draft = saveLoadSetting.value
  if (!state || !draft) return
  const scale = stageScales.saveLoad.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  if (state.target === 'name') {
    draft.Name_X = state.originX + dx
    draft.Name_Y = state.originY + dy
    return
  }
  if (state.target === 'date') {
    draft.Date_X = state.originX + dx
    draft.Date_Y = state.originY + dy
    return
  }
  if (state.target === 'thumb') {
    draft.Pic_X = state.originX + dx
    draft.Pic_Y = state.originY + dy
    return
  }
  if (state.target === 'page') {
    draft.PageX = state.originX + dx
    draft.PageY = state.originY + dy
    return
  }
  if (state.target === 'close') {
    draft.CloseButtonEx.X = state.originX + dx
    draft.CloseButtonEx.Y = state.originY + dy
    return
  }
  if (state.target === 'prev') {
    draft.PrePic.X = state.originX + dx
    draft.PrePic.Y = state.originY + dy
    return
  }
  if (state.target === 'next') {
    draft.NextPic.X = state.originX + dx
    draft.NextPic.Y = state.originY + dy
    return
  }
  draft.Viewport.X = state.originX + dx
  draft.Viewport.Y = state.originY + dy
}

function handleSaveLoadPreviewResizeMove(event: PointerEvent) {
  const state = saveLoadPreviewResizeState.value
  const draft = saveLoadSetting.value
  if (!state || !draft) return
  const scale = stageScales.saveLoad.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  draft.Viewport.Width = Math.max(1, state.originW + dx)
  draft.Viewport.Height = Math.max(1, state.originH + dy)
}

function handleTitlePreviewMove(event: PointerEvent) {
  const state = titlePreviewDragState.value
  const draft = titleSetting.value
  if (!state || !draft) return
  const button = draft.Buttons[state.index]
  if (!button) return
  const scale = stageScales.title.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  button.x = state.originX + dx
  button.y = state.originY + dy
}

function handleMenuPreviewMove(event: PointerEvent) {
  const state = menuPreviewDragState.value
  const draft = menuDraft.value
  if (!state || !draft) return
  const button = draft.Buttons[state.index]
  if (!button) return
  const scale = stageScales.menu.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  button.x = state.originX + dx
  button.y = state.originY + dy
}

function handleSystemPreviewMove(event: PointerEvent) {
  const state = systemPreviewDragState.value
  const draft = systemDraft.value
  if (!state || !draft) return
  const button = draft.Buttons[state.index]
  if (!button) return
  const scale = stageScales.system.value || 1
  const dx = Math.round((event.clientX - state.startX) / scale)
  const dy = Math.round((event.clientY - state.startY) / scale)
  button.x = state.originX + dx
  button.y = state.originY + dy
}

function handlePaymentPreviewMove(event: PointerEvent) {
  const state = paymentPreviewDragState.value
  const draft = paymentSetting.value
  if (!state || !draft) return
  const dx = Math.round((event.clientX - state.startX) / (stageScales.payment.value || 1))
  const dy = Math.round((event.clientY - state.startY) / (stageScales.payment.value || 1))
  if (state.target === 'decorate') {
    draft.Decorate_X = state.originX + dx
    draft.Decorate_Y = state.originY + dy
    return
  }
  if (state.target === 'area') {
    draft.GoodAreaBack_X = state.originX + dx
    draft.GoodAreaBack_Y = state.originY + dy
    return
  }
  if (state.target === 'flower') {
    draft.Flower_X = state.originX + dx
    draft.Flower_Y = state.originY + dy
    return
  }
  if (state.target === 'flower-own') {
    draft.FlowerOwn_X = state.originX + dx
    draft.FlowerOwn_Y = state.originY + dy
    return
  }
  if (state.target === 'flower-price') {
    draft.FlowerPrice_X = state.originX + dx
    draft.FlowerPrice_Y = state.originY + dy
    return
  }
  if (state.target === 'page') {
    draft.PageX = state.originX + dx
    draft.PageY = state.originY + dy
    return
  }
  const goods = state.goodsIndex !== undefined ? draft.GoodsList[state.goodsIndex] : null
  if (goods) {
    if (state.target === 'goods-back') {
      goods.GoodsBack_X = state.originX + dx
      goods.GoodsBack_Y = state.originY + dy
      return
    }
    if (state.target === 'goods-icon') {
      const x = state.originX + dx
      const y = state.originY + dy
      draft.GoodsList.forEach((item, index) => {
        draft.GoodsList[index] = {
          ...item,
          Preview_X: x,
          Preview_Y: y,
        }
      })
      return
    }
    if (state.target === 'goods-preview-back') {
      const x = state.originX + dx
      const y = state.originY + dy
      draft.GoodsList.forEach((item, index) => {
        draft.GoodsList[index] = {
          ...item,
          PreviewBack_X: x,
          PreviewBack_Y: y,
        }
      })
      return
    }
    if (state.target === 'goods-name') {
      goods.GoodsName_X = state.originX + dx
      goods.GoodsName_Y = state.originY + dy
      return
    }
    if (state.target === 'goods-intro') {
      goods.GoodIntroView.X = state.originX + dx
      goods.GoodIntroView.Y = state.originY + dy
      return
    }
    if (state.target === 'goods-flower') {
      goods.Flower_X = state.originX + dx
      goods.Flower_Y = state.originY + dy
      return
    }
    if (state.target === 'goods-price') {
      goods.GoodsPrice_X = state.originX + dx
      goods.GoodsPrice_Y = state.originY + dy
      return
    }
    if (state.target === 'goods-status') {
      const x = state.originX + dx
      const y = state.originY + dy
      draft.GoodsList.forEach((item, index) => {
        draft.GoodsList[index] = {
          ...item,
          BuyBtn: {
            ...item.BuyBtn,
            X: x,
            Y: y,
          },
        }
      })
      return
    }
  }
  const target =
    state.target === 'close'
      ? draft.ClosePic
      : state.target === 'prev'
        ? draft.PrePic
        : state.target === 'next'
          ? draft.NextPic
          : state.target === 'tag'
            ? draft.TagButton
            : draft.OneKeyGetBtn
  target.X = state.originX + dx
  target.Y = state.originY + dy
}

function handlePaymentPreviewResizeMove(event: PointerEvent) {
  const state = paymentPreviewResizeState.value
  const draft = paymentSetting.value
  if (!state || !draft) return
  const dx = Math.round((event.clientX - state.startX) / (stageScales.payment.value || 1))
  const dy = Math.round((event.clientY - state.startY) / (stageScales.payment.value || 1))
  if (state.target === 'area') {
    draft.GoodAreaBack_W = Math.max(1, state.originW + dx)
    draft.GoodAreaBack_H = Math.max(1, state.originH + dy)
    return
  }
  const goods = state.goodsIndex !== undefined ? draft.GoodsList[state.goodsIndex] : null
  if (!goods) return
  if (state.target === 'goods-back') {
    goods.GoodsBack_W = Math.max(1, state.originW + dx)
    goods.GoodsBack_H = Math.max(1, state.originH + dy)
    return
  }
  if (state.target === 'goods-icon') {
    const width = Math.max(1, state.originW + dx)
    const height = Math.max(1, state.originH + dy)
    draft.GoodsList.forEach((item, index) => {
      draft.GoodsList[index] = {
        ...item,
        Preview_W: width,
        Preview_H: height,
      }
    })
    return
  }
  if (state.target === 'goods-intro') {
    goods.GoodIntroView.Width = Math.max(1, state.originW + dx)
    goods.GoodIntroView.Height = Math.max(1, state.originH + dy)
    return
  }
  const width = Math.max(1, state.originW + dx)
  const height = Math.max(1, state.originH + dy)
  draft.GoodsList.forEach((item, index) => {
    draft.GoodsList[index] = {
      ...item,
      PreviewBack_W: width,
      PreviewBack_H: height,
    }
  })
}

function stopPreviewDrag() {
  previewDragState.value = null
  previewResizeState.value = null
  replayPreviewDragState.value = null
  replayPreviewResizeState.value = null
  saveLoadPreviewDragState.value = null
  saveLoadPreviewResizeState.value = null
  titlePreviewDragState.value = null
  menuPreviewDragState.value = null
  systemPreviewDragState.value = null
  paymentPreviewDragState.value = null
  paymentPreviewResizeState.value = null
  window.removeEventListener('pointermove', handlePreviewPointerMove)
  window.removeEventListener('pointermove', handlePreviewResizeMove)
  window.removeEventListener('pointermove', handleReplayPreviewMove)
  window.removeEventListener('pointermove', handleReplayPreviewResizeMove)
  window.removeEventListener('pointermove', handleSaveLoadPreviewMove)
  window.removeEventListener('pointermove', handleSaveLoadPreviewResizeMove)
  window.removeEventListener('pointermove', handleTitlePreviewMove)
  window.removeEventListener('pointermove', handleMenuPreviewMove)
  window.removeEventListener('pointermove', handleSystemPreviewMove)
  window.removeEventListener('pointermove', handlePaymentPreviewMove)
  window.removeEventListener('pointermove', handlePaymentPreviewResizeMove)
  window.removeEventListener('pointerup', stopPreviewDrag)
}

function beginPreviewPointerInteraction(moveHandler: (event: PointerEvent) => void, event: PointerEvent) {
  window.addEventListener('pointermove', moveHandler)
  window.addEventListener('pointerup', stopPreviewDrag)
  event.preventDefault()
}

function startPreviewDrag(target: 'name-box' | 'talk-box' | 'name-text' | 'talk-text', event: PointerEvent) {
  const draft = talkSetting.value
  if (!draft) return
  previewDragState.value = {
    target,
    startX: event.clientX,
    startY: event.clientY,
    originX:
      target === 'name-box'
        ? draft.NameWinS.x
        : target === 'name-text'
          ? draft.NameWinS.P_x
          : target === 'talk-text'
            ? draft.TalkWin.p_x1
            : draft.TalkWin.X,
    originY:
      target === 'name-box'
        ? draft.NameWinS.y
        : target === 'name-text'
          ? draft.NameWinS.P_y
          : target === 'talk-text'
            ? draft.TalkWin.p_y1
            : draft.TalkWin.Y,
  }
  beginPreviewPointerInteraction(handlePreviewPointerMove, event)
}

function startPreviewResize(event: PointerEvent) {
  const draft = talkSetting.value
  if (!draft) return
  previewResizeState.value = {
    startX: event.clientX,
    startY: event.clientY,
    originW: draft.TalkWin.p_viewW,
    originH: draft.TalkWin.p_viewH,
  }
  beginPreviewPointerInteraction(handlePreviewResizeMove, event)
}

function startReplayViewportDrag(event: PointerEvent) {
  const draft = replaySetting.value
  if (!draft) return
  replayPreviewDragState.value = {
    target: 'viewport',
    startX: event.clientX,
    startY: event.clientY,
    originX: draft.Viewport.X,
    originY: draft.Viewport.Y,
  }
  beginPreviewPointerInteraction(handleReplayPreviewMove, event)
}

function startReplayCloseDrag(event: PointerEvent) {
  const draft = replaySetting.value
  if (!draft) return
  replayPreviewDragState.value = {
    target: 'close',
    startX: event.clientX,
    startY: event.clientY,
    originX: draft.CloseEx.X,
    originY: draft.CloseEx.Y,
  }
  beginPreviewPointerInteraction(handleReplayPreviewMove, event)
}

function startReplayViewportResize(event: PointerEvent) {
  const draft = replaySetting.value
  if (!draft) return
  replayPreviewResizeState.value = {
    startX: event.clientX,
    startY: event.clientY,
    originW: draft.Viewport.Width,
    originH: draft.Viewport.Height,
  }
  beginPreviewPointerInteraction(handleReplayPreviewResizeMove, event)
}

function startSaveLoadPreviewDrag(target: 'viewport' | 'name' | 'date' | 'thumb' | 'close' | 'prev' | 'next' | 'page', event: PointerEvent) {
  const draft = saveLoadSetting.value
  if (!draft) return
  const source =
    target === 'name'
      ? { X: draft.Name_X, Y: draft.Name_Y }
      : target === 'date'
        ? { X: draft.Date_X, Y: draft.Date_Y }
        : target === 'thumb'
          ? { X: draft.Pic_X, Y: draft.Pic_Y }
          : target === 'page'
            ? { X: draft.PageX, Y: draft.PageY }
          : target === 'close'
        ? draft.CloseButtonEx
        : target === 'prev'
          ? draft.PrePic
          : target === 'next'
            ? draft.NextPic
            : draft.Viewport
  saveLoadPreviewDragState.value = {
    target,
    startX: event.clientX,
    startY: event.clientY,
    originX: Number(source.X ?? 0),
    originY: Number(source.Y ?? 0),
  }
  beginPreviewPointerInteraction(handleSaveLoadPreviewMove, event)
}

function startSaveLoadPreviewResize(event: PointerEvent) {
  const draft = saveLoadSetting.value
  if (!draft) return
  saveLoadPreviewResizeState.value = {
    startX: event.clientX,
    startY: event.clientY,
    originW: draft.Viewport.Width,
    originH: draft.Viewport.Height,
  }
  beginPreviewPointerInteraction(handleSaveLoadPreviewResizeMove, event)
}

function startTitleButtonDrag(index: number, event: PointerEvent) {
  const draft = titleSetting.value
  const button = draft?.Buttons?.[index]
  if (!draft || !button) return
  titlePreviewDragState.value = {
    index,
    startX: event.clientX,
    startY: event.clientY,
    originX: button.x,
    originY: button.y,
  }
  beginPreviewPointerInteraction(handleTitlePreviewMove, event)
}

function startMenuButtonDrag(index: number, event: PointerEvent) {
  const draft = menuDraft.value
  const button = draft?.Buttons?.[index]
  if (!draft || !button) return
  menuPreviewDragState.value = {
    index,
    startX: event.clientX,
    startY: event.clientY,
    originX: button.x,
    originY: button.y,
  }
  beginPreviewPointerInteraction(handleMenuPreviewMove, event)
}

function startSystemButtonDrag(index: number, event: PointerEvent) {
  const draft = systemDraft.value
  const button = draft?.Buttons?.[index]
  if (!draft || !button) return
  systemPreviewDragState.value = {
    index,
    startX: event.clientX,
    startY: event.clientY,
    originX: button.x,
    originY: button.y,
  }
  beginPreviewPointerInteraction(handleSystemPreviewMove, event)
}

function startPaymentPreviewDrag(
  target:
    | 'decorate'
    | 'area'
    | 'close'
    | 'prev'
    | 'next'
    | 'tag'
    | 'onekey'
    | 'page'
    | 'flower'
    | 'flower-own'
    | 'flower-price'
    | 'goods-back'
    | 'goods-icon'
    | 'goods-preview-back'
    | 'goods-name'
    | 'goods-intro'
    | 'goods-flower'
    | 'goods-price'
    | 'goods-status',
  event: PointerEvent,
  goodsIndex?: number,
) {
  const draft = paymentSetting.value
  if (!draft) return
  const goods = goodsIndex !== undefined ? draft.GoodsList[goodsIndex] : null
  const source =
    target === 'decorate'
      ? { X: draft.Decorate_X, Y: draft.Decorate_Y }
      : target === 'area'
        ? { X: draft.GoodAreaBack_X, Y: draft.GoodAreaBack_Y }
      : target === 'flower'
        ? { X: draft.Flower_X, Y: draft.Flower_Y }
      : target === 'flower-own'
        ? { X: draft.FlowerOwn_X, Y: draft.FlowerOwn_Y }
      : target === 'flower-price'
        ? { X: draft.FlowerPrice_X, Y: draft.FlowerPrice_Y }
      : target === 'page'
        ? { X: draft.PageX, Y: draft.PageY }
      : target === 'goods-back' && goods
        ? { X: goods.GoodsBack_X, Y: goods.GoodsBack_Y }
      : target === 'goods-icon' && goods
          ? { X: goods.Preview_X, Y: goods.Preview_Y }
      : target === 'goods-preview-back' && goods
        ? { X: goods.PreviewBack_X, Y: goods.PreviewBack_Y }
      : target === 'goods-name' && goods
        ? { X: goods.GoodsName_X, Y: goods.GoodsName_Y }
      : target === 'goods-intro' && goods
        ? { X: goods.GoodIntroView.X, Y: goods.GoodIntroView.Y }
      : target === 'goods-flower' && goods
        ? { X: goods.Flower_X, Y: goods.Flower_Y }
      : target === 'goods-price' && goods
        ? { X: goods.GoodsPrice_X, Y: goods.GoodsPrice_Y }
      : target === 'goods-status' && goods
        ? { X: goods.BuyBtn.X, Y: goods.BuyBtn.Y }
        : target === 'close'
          ? draft.ClosePic
          : target === 'prev'
            ? draft.PrePic
            : target === 'next'
              ? draft.NextPic
              : target === 'tag'
                ? draft.TagButton
                : draft.OneKeyGetBtn
  paymentPreviewDragState.value = {
    target,
    goodsIndex,
    startX: event.clientX,
    startY: event.clientY,
    originX: Number(source.X ?? 0),
    originY: Number(source.Y ?? 0),
  }
  beginPreviewPointerInteraction(handlePaymentPreviewMove, event)
}

function startPaymentPreviewResize(
  target: 'area' | 'goods-back' | 'goods-icon' | 'goods-preview-back' | 'goods-intro',
  event: PointerEvent,
  goodsIndex?: number,
) {
  const draft = paymentSetting.value
  if (!draft) return
  const goods = goodsIndex !== undefined ? draft.GoodsList[goodsIndex] : null
  const source =
    target === 'area'
      ? { Width: draft.GoodAreaBack_W, Height: draft.GoodAreaBack_H }
      : target === 'goods-back' && goods
        ? { Width: goods.GoodsBack_W, Height: goods.GoodsBack_H }
      : target === 'goods-icon' && goods
        ? { Width: goods.Preview_W, Height: goods.Preview_H }
      : target === 'goods-intro' && goods
        ? { Width: goods.GoodIntroView.Width, Height: goods.GoodIntroView.Height }
        : { Width: goods?.PreviewBack_W ?? 0, Height: goods?.PreviewBack_H ?? 0 }
  paymentPreviewResizeState.value = {
    target,
    goodsIndex,
    startX: event.clientX,
    startY: event.clientY,
    originW: Number(source.Width ?? 0),
    originH: Number(source.Height ?? 0),
  }
  beginPreviewPointerInteraction(handlePaymentPreviewResizeMove, event)
}

function updateNameDialogBackPic(value: string) {
  if (!talkSetting.value) return
  talkSetting.value.NameWinS.BackPic.name = value
}

function updateTalkDialogBackPic(value: string) {
  if (!talkSetting.value) return
  talkSetting.value.TalkWin.BackPic.name = value
}

function updateEnterSePath(value: string) {
  if (!totalSetting.value) return
  totalSetting.value.EnterSE.name = value
}

function updateMoveSePath(value: string) {
  if (!totalSetting.value) return
  totalSetting.value.MoveSE.name = value
}

function updateDefaultFontPath(value: string) {
  if (!totalSetting.value) return
  totalSetting.value.FontName = value
  void syncDraftFontPreview(value)
}

function updateSaveLoadFontPath(value: string) {
  if (!saveLoadDraft.value) return
  saveLoadFontDirty.value = true
  saveLoadDraft.value.Font = value
  void syncSaveLoadFontPreview(value)
}

function updateSaveLoadFontSize(value: number) {
  if (!saveLoadDraft.value) return
  saveLoadFontDirty.value = true
  saveLoadDraft.value.FontSize = value
}

function applyMallGoodsLayout(target: MallGoodsDraft, source: MallGoodsDraft): void {
  target.GoodsBack_X = Number(source.GoodsBack_X ?? 0)
  target.GoodsBack_Y = Number(source.GoodsBack_Y ?? 0)
  target.GoodsBack_W = Math.max(1, Number(source.GoodsBack_W ?? 1))
  target.GoodsBack_H = Math.max(1, Number(source.GoodsBack_H ?? 1))
  target.Preview_X = Number(source.Preview_X ?? 0)
  target.Preview_Y = Number(source.Preview_Y ?? 0)
  target.Preview_W = Math.max(1, Number(source.Preview_W ?? 1))
  target.Preview_H = Math.max(1, Number(source.Preview_H ?? 1))
  target.PreviewBack_X = Number(source.PreviewBack_X ?? 0)
  target.PreviewBack_Y = Number(source.PreviewBack_Y ?? 0)
  target.PreviewBack_W = Math.max(1, Number(source.PreviewBack_W ?? 1))
  target.PreviewBack_H = Math.max(1, Number(source.PreviewBack_H ?? 1))
  target.GoodsName_X = Number(source.GoodsName_X ?? 0)
  target.GoodsName_Y = Number(source.GoodsName_Y ?? 0)
  target.GoodIntroView = { ...source.GoodIntroView }
  target.Flower_X = Number(source.Flower_X ?? 0)
  target.Flower_Y = Number(source.Flower_Y ?? 0)
  target.GoodsPrice_X = Number(source.GoodsPrice_X ?? 0)
  target.GoodsPrice_Y = Number(source.GoodsPrice_Y ?? 0)
  target.BuyBtn = {
    ...target.BuyBtn,
    X: Number(source.BuyBtn?.X ?? 0),
    Y: Number(source.BuyBtn?.Y ?? 0),
  }
}

function createDefaultMallGoodsLayout(index: number): MallGoodsDraft {
  const model = new GameMallNewModel('255,255,255', {
    Width: projectScreenWidth.value,
    Height: projectScreenHeight.value,
  })
  return createMallGoodsDraft(model.GoodsList[index % MALL_GOODS_PER_PAGE] ?? model.GoodsList[0])
}

function createEmptyMallGoodsDraft(index: number, template?: MallGoodsDraft, layout?: MallGoodsDraft): MallGoodsDraft {
  const next = template ? JSON.parse(JSON.stringify(template)) as MallGoodsDraft : createMallGoodsDraft(new Goods())
  next.GoodsId = index + 1
  next.GoodsName = '预售新品'
  next.GoodsType = GoodsTypeEnum.Map
  next.GoodsIntro = ''
  next.Good_Status = GoodsStatus.NotUp
  next.CanBuyRepeat = false
  if (layout) {
    applyMallGoodsLayout(next, layout)
    return next
  }
  if (!template) {
    const goodsBackWidth = Math.max(1, Number(next.GoodsBack_W || 160))
    const goodsBackHeight = Math.max(1, Number(next.GoodsBack_H || 92))
    const previewColumnRight = Math.max(
      Number(next.PreviewBack_X || 0) + Math.max(1, Number(next.PreviewBack_W || 54)),
      Number(next.Preview_X || 0) + Math.max(1, Number(next.Preview_W || 48)),
    )
    const textX = Math.min(Math.max(previewColumnRight + 8, 62), Math.max(12, goodsBackWidth - 84))
    const textWidth = Math.max(72, goodsBackWidth - textX - 8)

    next.GoodsName_X = textX
    next.GoodsName_Y = 10
    next.GoodIntroView.X = textX
    next.GoodIntroView.Y = 32
    next.GoodIntroView.Width = textWidth
    next.GoodIntroView.Height = Math.max(24, Math.min(30, goodsBackHeight - 40))
    next.Flower_X = textX
    next.Flower_Y = Math.max(0, goodsBackHeight - 28)
    next.GoodsPrice_X = Math.min(textX + 18, Math.max(12, goodsBackWidth - 56))
    next.GoodsPrice_Y = Math.max(0, goodsBackHeight - 27)
  }
  return next
}

function resizeMallGoodsList(nextValue: number) {
  if (!paymentDraft.value) return
  const size = Math.min(MAX_MALL_GOODS_COUNT, Math.max(1, Math.floor(Number(nextValue) || 1)))
  const current = paymentDraft.value.GoodsList.slice(0, size)
  while (current.length < size) {
    const layout = current[current.length % MALL_GOODS_PER_PAGE] ?? createDefaultMallGoodsLayout(current.length)
    current.push(createEmptyMallGoodsDraft(current.length, current[0], layout))
  }
  current.forEach((item, index) => {
    if (!item.GoodsId) item.GoodsId = index + 1
    if (!item.GoodsName.trim()) item.GoodsName = `商品${index + 1}`
  })
  paymentDraft.value.GoodsList = current
}

const mallGoodsCount = computed({
  get: () => Math.max(1, paymentDraft.value?.GoodsList.length ?? 1),
  set: (value: number) => resizeMallGoodsList(value),
})

const generalTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  totalSetting: totalSetting.value!,
  projectNameDraftRef: projectNameDraft,
  previewFont: previewFont.value,
  previewSize: previewSize.value,
  messageColorHexRef: messageColorHex,
  uiColorHexRef: uiColorHex,
  menuOptions: MENU_OPTIONS,
  updateDefaultFontPath,
  updateEnterSePath,
  updateMoveSePath,
}))

const titleTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  titleSetting: titleSetting.value!,
  imageOptionPickerItems: imageOptionPickerItems.value,
  backgroundGraphicBaseFolderPath: TITLE_BACKGROUND_BASE_FOLDER,
  musicBaseFolderPath: TITLE_MUSIC_BASE_FOLDER,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  titleBackgroundUrl: titleBackgroundUrl.value,
  titlePreviewButtons: titlePreviewButtons.value,
  previewGuideActive: titlePreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.title,
  updateTitleBackgroundPic,
  updateTitleBgmPath,
  startTitleButtonDrag,
  activeTitleDragIndex: titlePreviewDragState.value?.index ?? -1,
}))

const menuTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  menuSetting: menuSetting.value!,
  imageOptionPickerItems: imageOptionPickerItems.value,
  backgroundGraphicBaseFolderPath: SETTINGS_BACKGROUND_BASE_FOLDER,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  menuBackgroundUrl: menuBackgroundUrl.value,
  menuPreviewButtons: menuPreviewButtons.value,
  previewGuideActive: menuPreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.menu,
  updateMenuBackPic,
  startMenuButtonDrag,
  activeMenuDragIndex: menuPreviewDragState.value?.index ?? -1,
}))

const dialogTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  talkSetting: talkSetting.value!,
  imageOptionPickerItems: imageOptionPickerItems.value,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  graphicBaseFolderPath: DIALOG_GRAPHIC_BASE_FOLDER,
  previewNameBoxUrl: previewNameBoxUrl.value,
  previewTalkBoxUrl: previewTalkBoxUrl.value,
  previewNameBoxStyle: previewNameBoxStyle.value,
  previewNameTextStyle: previewNameTextStyle.value,
  previewNameTextHandleStyle: previewNameTextHandleStyle.value,
  previewTalkBoxStyle: previewTalkBoxStyle.value,
  previewTalkTextRegionStyle: previewTalkTextRegionStyle.value,
  previewSampleContent: previewSampleContent.value,
  previewGuideActive: previewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.dialog,
  updateNameDialogBackPic,
  updateTalkDialogBackPic,
  startPreviewDrag,
  startPreviewResize,
}))

const imageOptionTabProps = computed(() => ({
  imageOptionButtons: imageOptionButtons.value,
  graphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  imageOptionMaxCountRef: imageOptionMaxCount,
  imageOptionSelectedIndexRef: imageOptionSelectedIndex,
  imageOptionHoverRef: imageOptionHover,
  selectedImageOption: selectedImageOption.value,
  isReservedImageOption: isReservedImageOption.value,
  selectedImageOptionNoneUrl: selectedImageOptionNoneUrl.value,
  selectedImageOptionMoveUrl: selectedImageOptionMoveUrl.value,
  imageOptionTestUrl: imageOptionTestUrl.value,
  showImageOptionButtonTest: showImageOptionButtonTest.value,
  selectImageOption,
  formatImageOptionLabel,
  updateSelectedImageOptionNonePic,
  updateSelectedImageOptionMovePic,
}))

const replayTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  replaySetting: replaySetting.value!,
  backgroundGraphicBaseFolderPath: SETTINGS_BACKGROUND_BASE_FOLDER,
  imageOptionPickerItems: imageOptionPickerItems.value,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  replayBackgroundUrl: replayBackgroundUrl.value,
  replayCloseButtonUrl: replayCloseButtonUrl.value,
  replayPreviewFont: previewFont.value,
  replayPreviewFontSize: Math.max(12, Number(totalSetting.value?.FontSize ?? 30)),
  replayPreviewTextColor: previewMessageColorCss.value,
  replayPreviewViewportStyle: replayPreviewViewportStyle.value,
  replayPreviewCloseStyle: replayPreviewCloseStyle.value,
  previewGuideActive: replayPreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.replay,
  updateReplayBackPic,
  updateReplayButtonIndex,
  startReplayViewportDrag,
  startReplayCloseDrag,
  startReplayViewportResize,
}))

const saveLoadTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  saveLoadSetting: saveLoadSetting.value!,
  imageOptionPickerItems: imageOptionPickerItems.value,
  graphicBaseFolderPath: DIALOG_GRAPHIC_BASE_FOLDER,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  saveLoadBackgroundUrl: saveLoadBackgroundUrl.value,
  saveLoadBackButtonUrl: saveLoadBackButtonUrl.value,
  saveLoadPreviewThumbUrl: saveLoadPreviewThumbUrl.value,
  saveLoadCloseButtonUrl: saveLoadCloseButtonUrl.value,
  saveLoadPrevButtonUrl: saveLoadPrevButtonUrl.value,
  saveLoadNextButtonUrl: saveLoadNextButtonUrl.value,
  saveLoadBackButtonStyle: saveLoadBackButtonStyle.value,
  saveLoadCloseButtonStyle: saveLoadCloseButtonStyle.value,
  saveLoadPrevButtonStyle: saveLoadPrevButtonStyle.value,
  saveLoadNextButtonStyle: saveLoadNextButtonStyle.value,
  saveLoadPreviewViewportStyle: saveLoadPreviewViewportStyle.value,
  saveLoadPreviewPageTextStyle: saveLoadPreviewPageTextStyle.value,
  saveLoadPreviewPageText: saveLoadPreviewPageText.value,
  saveLoadPreviewSlots: saveLoadPreviewSlots.value,
  saveLoadPreviewFont: saveLoadPreviewFont.value,
  saveLoadPreviewFontSize: saveLoadPreviewFontSize.value,
  saveLoadStepperDisplaySize: saveLoadStepperDisplaySize.value,
  saveLoadPreviewTextColor: previewUiColorCss.value,
  previewGuideActive: saveLoadPreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.saveLoad,
  updateSaveLoadBackPic: (value: string) => {
    if (!saveLoadSetting.value) return
    saveLoadSetting.value.BackPic.name = value
  },
  updateSaveLoadFontPath,
  updateSaveLoadFontSize,
  startSaveLoadPreviewDrag,
  startSaveLoadPreviewResize,
}))

const systemTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  systemSetting: systemSetting.value!,
  imageOptionPickerItems: imageOptionPickerItems.value,
  backgroundGraphicBaseFolderPath: SETTINGS_BACKGROUND_BASE_FOLDER,
  uiGraphicBaseFolderPath: DIALOG_GRAPHIC_BASE_FOLDER,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  systemBackgroundUrl: systemBackgroundUrl.value,
  systemBarNoneUrl: systemBarNoneUrl.value,
  systemBarMoveUrl: systemBarMoveUrl.value,
  systemPreviewButtons: systemPreviewButtons.value,
  systemPreviewAudioItems: systemPreviewAudioItems.value,
  previewGuideActive: systemPreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.system,
  updateSystemBackPic,
  updateSystemBarNonePic,
  updateSystemBarMovePic,
  startSystemButtonDrag,
  activeSystemDragIndex: systemPreviewDragState.value?.index ?? -1,
}))

const paymentTabProps = computed(() => ({
  screenWidth: projectScreenWidth.value,
  screenHeight: projectScreenHeight.value,
  paymentSetting: paymentSetting.value!,
  mallGoodsCountRef: mallGoodsCount,
  imageOptionPickerItems: imageOptionPickerItems.value,
  graphicBaseFolderPath: PAYMENT_GRAPHIC_BASE_FOLDER,
  imageOptionGraphicBaseFolderPath: IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  previewGuideActive: paymentPreviewDragState.value !== null,
  onPreviewStageResize: resizeHandlers.payment,
  startPaymentPreviewDrag,
  startPaymentPreviewResize,
  activePaymentDragTarget: paymentPreviewDragState.value?.target ?? '',
}))

const previewButtonImageUrls = computed(() => {
  const urls = new Set<string>()
  for (const item of titleSetting.value?.Buttons ?? []) {
    if (!item.enabled || item.index <= 0) continue
    const url = resolveImageOptionPreviewUrl(item.index)
    if (url) urls.add(url)
  }
  for (const item of menuSetting.value?.Buttons ?? []) {
    if (!item.enabled || item.index <= 0) continue
    const url = resolveImageOptionPreviewUrl(item.index)
    if (url) urls.add(url)
  }
  for (const item of systemSetting.value?.Buttons ?? []) {
    if (!item.enabled || item.index <= 0) continue
    const url = resolveImageOptionPreviewUrl(item.index)
    if (url) urls.add(url)
  }
  ;[saveLoadBackButtonUrl.value, saveLoadCloseButtonUrl.value, saveLoadPrevButtonUrl.value, saveLoadNextButtonUrl.value]
    .filter(Boolean)
    .forEach((url) => urls.add(url))
  ;[
    paymentCloseButtonUrl.value,
    paymentPrevButtonUrl.value,
    paymentNextButtonUrl.value,
    paymentTagButtonUrl.value,
    paymentOneKeyGetButtonUrl.value,
  ].filter(Boolean).forEach((url) => urls.add(url))
  return Array.from(urls)
})

watch(previewNameBoxUrl, async (url) => {
  previewNameImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(previewTalkBoxUrl, async (url) => {
  previewTalkImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(systemBarNoneUrl, async (url) => {
  previewSystemBarNoneImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(systemBarMoveUrl, async (url) => {
  previewSystemBarMoveImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(replayCloseButtonUrl, async (url) => {
  previewReplayCloseImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(paymentDecorateUrl, async (url) => {
  paymentDecorateImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(paymentGoodAreaBackUrl, async (url) => {
  paymentGoodAreaImageSize.value = await loadPreviewImageSize(url)
}, { immediate: true })

watch(previewButtonImageUrls, async (urls) => {
  const entries = await Promise.all(
    urls.map(async (url) => [url, await loadPreviewImageSize(url)] as const),
  )
  previewTitleButtonImageSizes.value = Object.fromEntries(entries)
}, { immediate: true })

onMounted(() => {
  void refreshSettingsPortraitChromeMetrics()
  window.addEventListener('resize', handleSettingsViewportResize, { passive: true })
  window.visualViewport?.addEventListener('resize', handleSettingsViewportResize, { passive: true })
})

onBeforeUnmount(() => {
  stopPreviewDrag()
  window.removeEventListener('resize', handleSettingsViewportResize)
  window.visualViewport?.removeEventListener('resize', handleSettingsViewportResize)
})
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="设置"
    :class="settingsModalClass"
    :style="settingsModalPortraitStyle"
    :mask-closable="false"
    :auto-focus="false"
    @update:show="(value) => { if (!value) closeWithCancel() }"
  >
    <div
      v-if="project && totalSetting && titleSetting && menuSetting && talkSetting && replaySetting && saveLoadSetting && systemSetting && paymentSetting"
      class="settings-shell"
      :class="{ 'settings-shell--portrait': isSettingsPortraitLayout }"
      :style="settingsShellStyle"
    >
      <template v-if="isSettingsPortraitLayout">
        <div class="settings-portrait-content">
          <SettingsGeneralTab v-if="settingsTab === 'general'" v-bind="generalTabProps" portrait-layout />
          <SettingsTitleTab
            v-else-if="settingsTab === 'title'"
            v-bind="titleTabProps"
            portrait-layout
          />
          <SettingsMenuTab v-else-if="settingsTab === 'menu'" v-bind="menuTabProps" portrait-layout />
          <SettingsDialogTab v-else-if="settingsTab === 'dialog'" v-bind="dialogTabProps" portrait-layout />
          <SettingsImageOptionTab v-else-if="settingsTab === 'image-option'" v-bind="imageOptionTabProps" portrait-layout />
          <SettingsReplayTab v-else-if="settingsTab === 'replay'" v-bind="replayTabProps" portrait-layout />
          <SettingsSaveLoadTab v-else-if="settingsTab === 'save-load'" v-bind="saveLoadTabProps" portrait-layout />
          <SettingsSystemTab v-else-if="settingsTab === 'system'" v-bind="systemTabProps" portrait-layout />
          <SettingsPaymentTab
            v-else-if="ENABLE_MALL_SETTINGS && settingsTab === 'payment'"
            v-bind="paymentTabProps"
            portrait-layout
          />
        </div>
      </template>
      <n-tabs
        v-else
        v-model:value="settingsTab"
        type="line"
        :animated="false"
        display-directive="if"
        class="settings-tabs"
      >
        <n-tab-pane name="general" tab="总设">
          <SettingsGeneralTab v-bind="generalTabProps" />
        </n-tab-pane>

        <n-tab-pane name="title" tab="标题画面">
          <SettingsTitleTab v-bind="titleTabProps" />
        </n-tab-pane>

        <n-tab-pane name="menu" tab="工程菜单">
          <SettingsMenuTab v-bind="menuTabProps" />
        </n-tab-pane>

        <n-tab-pane name="dialog" tab="对话框">
          <SettingsDialogTab v-bind="dialogTabProps" />
        </n-tab-pane>

        <n-tab-pane name="image-option" tab="图片选项">
          <SettingsImageOptionTab v-bind="imageOptionTabProps" />
        </n-tab-pane>

        <n-tab-pane name="replay" tab="剧情回放">
          <SettingsReplayTab v-bind="replayTabProps" />
        </n-tab-pane>

        <n-tab-pane name="save-load" tab="存档/读档">
          <SettingsSaveLoadTab v-bind="saveLoadTabProps" />
        </n-tab-pane>

        <n-tab-pane name="system" tab="系统设置">
          <SettingsSystemTab v-bind="systemTabProps" />
        </n-tab-pane>

        <n-tab-pane v-if="ENABLE_MALL_SETTINGS" name="payment" tab="商城">
          <SettingsPaymentTab v-bind="paymentTabProps" />
        </n-tab-pane>
      </n-tabs>

      <div class="settings-actions">
        <n-button size="small" @click="applyChanges">应用</n-button>
        <n-button size="small" type="primary" @click="closeWithConfirm">确认</n-button>
        <n-button size="small" @click="closeWithCancel">取消</n-button>
      </div>
      <div v-if="isSettingsPortraitLayout" class="settings-portrait-tabs">
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'general' }"
          @click="settingsTab = 'general'"
        >
          总设
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'title' }"
          @click="settingsTab = 'title'"
        >
          标题
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'menu' }"
          @click="settingsTab = 'menu'"
        >
          菜单
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'dialog' }"
          @click="settingsTab = 'dialog'"
        >
          对话
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'image-option' }"
          @click="settingsTab = 'image-option'"
        >
          选项
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'replay' }"
          @click="settingsTab = 'replay'"
        >
          回放
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'save-load' }"
          @click="settingsTab = 'save-load'"
        >
          存读档
        </button>
        <button
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'system' }"
          @click="settingsTab = 'system'"
        >
          系统
        </button>
        <button
          v-if="ENABLE_MALL_SETTINGS"
          type="button"
          class="settings-portrait-tab"
          :class="{ 'settings-portrait-tab--active': settingsTab === 'payment' }"
          @click="settingsTab = 'payment'"
        >
          商城
        </button>
      </div>
    </div>
  </n-modal>

</template>

<style scoped>
.settings-shell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: min(760px, calc(96vh - 96px));
  min-height: 640px;
  background: var(--theme-bg-panel);
  overflow: hidden;
}

.settings-shell--portrait {
  gap: 0;
  flex: 1 1 auto;
  display: grid;
  grid-template-rows: var(--settings-portrait-content-h, minmax(0, 1fr)) var(--settings-portrait-tabs-h, 38px) var(--settings-portrait-actions-h, 34px);
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}

.settings-portrait-content {
  grid-row: 1;
  width: 100%;
  height: var(--settings-portrait-content-h);
  min-height: 0;
  overflow: hidden;
}

.settings-tabs {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.settings-tabs :deep(.n-tabs-nav) {
  flex: 0 0 auto;
  margin-bottom: 0 !important;
}

.settings-tabs :deep(.n-tabs-tab) {
  padding-left: 10px;
  padding-right: 10px;
}

.settings-tabs :deep(.n-tabs-pane-wrapper),
.settings-tabs :deep(.n-tabs-content),
.settings-tabs :deep(.n-tab-pane) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.settings-shell--portrait .settings-tabs {
  grid-row: 1;
  gap: 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.settings-shell--portrait .settings-tabs :deep(.n-tabs-nav) {
  order: 2;
  flex: 0 0 36px;
  border-top: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
}

.settings-shell--portrait .settings-tabs :deep(.n-tabs-pane-wrapper) {
  order: 1;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.settings-shell--portrait .settings-tabs :deep(.n-tabs-content),
.settings-shell--portrait .settings-tabs :deep(.n-tab-pane) {
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.settings-shell--portrait .settings-tabs :deep(.n-tabs-nav-scroll-content) {
  width: 100%;
  display: grid !important;
  grid-auto-flow: column;
  grid-auto-columns: minmax(72px, 1fr);
}

.settings-shell--portrait .settings-tabs :deep(.n-tabs-tab) {
  justify-content: center;
  padding-left: 6px;
  padding-right: 6px;
}

.settings-shell--portrait :deep(.settings-page) {
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.settings-shell--portrait .settings-actions {
  grid-row: 3;
  position: static;
  z-index: 6;
  height: var(--settings-portrait-actions-h);
  min-height: var(--settings-portrait-actions-h);
  align-items: center;
  padding: 4px 8px;
  background: var(--theme-bg-panel);
  box-sizing: border-box;
}

.settings-portrait-tabs {
  grid-row: 2;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(64px, 1fr);
  height: var(--settings-portrait-tabs-h);
  min-height: var(--settings-portrait-tabs-h);
  overflow-x: auto;
  overflow-y: hidden;
  background: var(--theme-bg-raised);
  border-top: 1px solid var(--theme-border);
}

.settings-portrait-tab {
  min-width: 0;
  padding: 0 8px;
  border: 0;
  border-right: 1px solid var(--theme-border);
  color: var(--theme-text-secondary);
  font-size: 12px;
  line-height: 37px;
  text-align: center;
  background: var(--theme-bg-raised);
}

.settings-portrait-tab--active {
  color: var(--theme-accent);
  background: var(--theme-bg-panel);
  font-weight: 600;
}


.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--theme-border);
}


:global(body .modal-settings.n-modal) {
  width: min(1180px, 98vw);
  max-width: 98vw;
  box-sizing: border-box;
  overflow: hidden;
}

:global(body .modal-settings--portrait.n-modal) {
  width: 100vw;
  max-width: 100vw;
  height: var(--settings-viewport-h, 100dvh);
  max-height: var(--settings-viewport-h, 100dvh);
}

:global(body .modal-settings--portrait .n-card__content) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: var(--settings-portrait-body-h, calc(100dvh - var(--settings-modal-header-h, 47px)));
  padding: 0 !important;
  box-sizing: border-box;
  overflow: hidden;
}

:global(body .modal-settings .n-card-header) {
  border-bottom: 1px solid var(--theme-border);
  padding-top: 8px;
  padding-bottom: 6px;
  min-height: 0;
}

:global(body .modal-settings .n-card-header__main) {
  font-size: var(--app-font-size-lg);
  line-height: var(--app-line-height-tight);
}

:global(body .modal-settings .n-card__content) {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .settings-shell {
    height: min(82vh, 760px);
    min-height: 0;
  }

  .settings-tabs :deep(.n-tabs-nav-scroll-content) {
    flex-wrap: wrap;
  }

  .settings-tabs :deep(.n-tabs-tab) {
    padding-left: 8px;
    padding-right: 8px;
  }

  .settings-tabs :deep(.n-tab-pane) {
    overflow: auto;
  }

  .settings-actions {
    flex-wrap: wrap;
  }

  :global(body .modal-settings.n-modal) {
    width: min(1080px, 96vw);
    max-width: 96vw;
  }
}

@media (max-width: 900px) {
  :global(body .modal-settings.n-modal:not(.modal-settings--portrait)) {
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
  }

  .settings-shell:not(.settings-shell--portrait) {
    height: calc(100vh - 96px);
    min-height: 0;
  }
}

@media (max-width: 1180px) and (orientation: portrait) {
  :global(body .modal-settings.n-modal) {
    width: 100vw;
    max-width: 100vw;
    height: var(--settings-viewport-h, 100dvh);
    max-height: var(--settings-viewport-h, 100dvh);
  }

  :global(body .modal-settings .n-card__content) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: var(--settings-portrait-body-h, calc(100dvh - var(--settings-modal-header-h, 47px)));
    padding: 0 !important;
    box-sizing: border-box;
    overflow: hidden;
  }

  .settings-shell {
    flex: 1 1 auto;
    display: grid;
    grid-template-rows: var(--settings-portrait-content-h, minmax(0, 1fr)) var(--settings-portrait-tabs-h, 38px) var(--settings-portrait-actions-h, 34px);
    height: 100%;
    min-height: 0;
  }

  .settings-tabs :deep(.n-tabs-nav) {
    order: 2;
    flex: 0 0 36px;
    border-top: 1px solid var(--theme-border);
    background: var(--theme-bg-raised);
  }

  .settings-tabs {
    min-height: 0;
  }

  .settings-actions {
    position: static;
    z-index: 6;
    height: var(--settings-portrait-actions-h, 34px);
    min-height: var(--settings-portrait-actions-h, 34px);
    align-items: center;
    padding: 4px 8px;
    background: var(--theme-bg-panel);
    box-sizing: border-box;
  }

  .settings-tabs :deep(.n-tabs-pane-wrapper) {
    order: 1;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .settings-tabs :deep(.n-tabs-content),
  .settings-tabs :deep(.n-tab-pane) {
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .settings-tabs :deep(.n-tabs-nav-scroll-content) {
    width: 100%;
    display: grid !important;
    grid-auto-flow: column;
    grid-auto-columns: minmax(72px, 1fr);
  }

  .settings-tabs :deep(.n-tabs-tab) {
    justify-content: center;
    padding-left: 6px;
    padding-right: 6px;
  }

}
</style>
